import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  getStandupBoard,
  getStandupTickets,
  getStandupUrgent,
  putStandupFocuses,
  setFocusDone,
  type BoardMember,
  type StandupBoard,
  type StandupTickets,
  type StandupTicket,
  type Focus,
  type UrgentResult,
  type UrgentTicket,
  type SlaState,
  type TicketWatchDigestItem,
  type TicketWatchBriefItem,
  type TicketDraft,
  runTicketWatch,
  getScoringConfig,
  putScoringConfig,
  type ScoringConfig,
  getStandupNotes,
  raiseStandupNote,
  deleteStandupNote,
  type StandupNote,
  getPriorities,
  getTicketTransitions,
  setTicketPriority,
  setTicketTransition,
  type JiraPriority,
  type JiraTransition,
} from "../../lib/api";
import { useProfile } from "../../lib/profile";
import { useAgent, useAgentSnapshot } from "../../agent/AgentProvider";
import { ProjectsTab } from "./ProjectsTab";
import {
  getStandupBrief,
  answerQuestion,
  dismissQuestion,
  getWeather,
  reviewPending,
  approveMemory,
  rejectMemory,
  getAaronProjects,
  getAaronVersion,
  type StandupBrief,
  type StandupQuestion,
  type Weather,
} from "../../lib/brain-api";
import type { PendingItem } from "../../lib/brain-types";
import { Icon, Spinner } from "../../design/ui";

// docs/standup/ — per-person standup board, rebuilt to the high-fidelity
// "Standup Page Redesign" handoff. Presentation only; all data wiring +
// interactions preserved. Tokens: brand teal #008e92 == var(--color-primary);
// mono == var(--font-mono). Remaining spec hexes (the bespoke status palette,
// quiet card backgrounds, subtle borders) live in the scoped SN_CSS block
// below — they are page-local, not promoted to the shared design system.

const POLL_MS = 20_000;

function newId(): string {
  try { return crypto.randomUUID(); } catch { return `f-${Date.now()}-${Math.round(Math.random() * 1e6)}`; }
}

// Stable per-person accent colour (avatar + tab) from the userId.
const AVATAR_COLORS = ["#4b7b8f", "#8f5e4b", "#6b5e9e", "#4b8f6e", "#9e6b8a", "#5e7a9e", "#9e8a4b", "#7a4b8f", "#4b6e8f"];
export function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
// Soft, modern avatar tint for the facepile (light fill + readable same-hue
// initials) — distinct from colorFor's solid-on-white avatars used elsewhere.
const AVATAR_TINTS = [
  { bg: "#e8ecf4", fg: "#4a5a7a" }, { bg: "#f1e8e4", fg: "#90543f" },
  { bg: "#ece6f5", fg: "#6b5499" }, { bg: "#e1eef0", fg: "#3a7e85" },
  { bg: "#f4ecdb", fg: "#876528" }, { bg: "#e5efe8", fg: "#437a5c" },
  { bg: "#f2e6ee", fg: "#8e577b" }, { bg: "#e8ebef", fg: "#566069" },
];
export function avatarTint(id: string): { bg: string; fg: string } {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length];
}
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

type DotColor = "red" | "amber" | "green" | "";

/** ticket state → tab dot colour (worst-state wins). */
function dotFor(t: StandupTickets | null | undefined): DotColor {
  if (!t || t.unmapped) return "";
  if (t.blocked.length) return "red";
  if (t.active.length) return "amber";
  return "green";
}

// ---------------------------------------------------------------------------
// Scoped stylesheet — exact handoff spec, page-local. Brand teal + mono come
// from app tokens; the bespoke status palette / quiet surfaces stay here.
// ---------------------------------------------------------------------------
const SN_CSS = `
.sn-page{font-family:var(--font-ui,'Roboto',system-ui,sans-serif);width:100%;max-width:none;padding:26px 30px 60px;}
.sn-page *{box-sizing:border-box;}

/* ---- header band ---- */
.sn-band{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;padding-bottom:22px;}
.sn-clock{font-family:var(--font-mono);font-size:48px;font-weight:500;color:#15252b;letter-spacing:-0.01em;line-height:1;font-variant-numeric:tabular-nums;}
.sn-date{font-size:14.5px;color:#6c777c;margin-top:9px;}
.sn-band-right{display:flex;align-items:center;gap:14px;}
.sn-chip{display:flex;align-items:center;gap:8px;padding:7px 13px;border-radius:20px;}
.sn-chip-dot{width:8px;height:8px;border-radius:50%;}
.sn-chip-label{font-size:13px;font-weight:500;}
.sn-chip.open{background:#e6f4ed;color:#2f7d57;}
.sn-chip.open .sn-chip-dot{background:#2f9e6b;}
.sn-chip.closed{background:#f0f2f3;color:#8a99a0;}
.sn-chip.closed .sn-chip-dot{background:#b8c2c7;}
.sn-statebtn{border-radius:7px;padding:9px 16px;font-size:13.5px;font-weight:500;cursor:pointer;transition:background .12s;}
.sn-statebtn:disabled{opacity:.6;cursor:default;}
.sn-statebtn.open{background:var(--color-primary);color:#fff;border:none;}
.sn-statebtn.open:hover:not(:disabled){background:#00787c;}
.sn-statebtn.closed{background:#fff;color:#0a6e72;border:1px solid var(--color-primary);}
.sn-statebtn.closed:hover:not(:disabled){background:#e3f1f1;}

/* ---- Chrome-style unified tab strip (Tickets · Brain · | · person tabs) ----
   One row that MERGES with the keyline beneath it. The active tab shares the
   content panel's background, is raised, and INTERRUPTS the keyline under it (it
   connects to the content); inactive tabs sit above the continuous keyline.
   The strip never horizontal-scrolls — container queries progressively hide the
   person-tab NAMES (→ avatar-only, full name via title) as width tightens. */
.sn-tabwrap{container-type:inline-size;position:relative;margin-bottom:22px;}
.sn-tabstrip{display:flex;align-items:flex-end;gap:3px;min-width:0;
  /* the continuous keyline lives on the strip's bottom edge */
  border-bottom:1px solid #d9dee0;padding:0 2px;
  /* hard guard: never show a horizontal scrollbar — tabs shrink/collapse first
     (below), and in the pathological tiny+many case the spacer clips, not scrolls */
  overflow:hidden;}
.sn-tabdiv{width:1px;align-self:center;height:20px;background:#dde2e4;margin:0 7px 6px;flex:none;}
.sn-tabspacer{flex:1 1 0;min-width:0;}
.sn-tab{position:relative;display:flex;align-items:center;gap:8px;cursor:pointer;
  flex:0 1 auto;min-width:0;background:none;border:none;font-family:inherit;
  padding:9px 14px;margin-bottom:-1px; /* overlap the keyline so active can break it */
  border:1px solid transparent;border-bottom:none;border-radius:8px 8px 0 0;
  color:#56656b;font-size:13.5px;font-weight:500;transition:color .12s ease,background .12s ease;}
.sn-tab:hover:not(.active){color:#15252b;background:#f1f4f5;}
.sn-tab:focus-visible{outline:2px solid var(--color-primary);outline-offset:-2px;}
.sn-tab.active{color:#15252b;background:#fff;border-color:#d9dee0;
  /* hide the strip keyline directly under the active tab → connects to content */
  box-shadow:0 1px 0 0 #fff;}
.sn-tab.active.urgent{color:#a13a40;}
.sn-tab.active.brain{color:#0a6e72;}
.sn-tab i{font-size:16px;line-height:1;flex:none;}
.sn-tab-mark{width:20px;height:20px;border-radius:6px;background:var(--color-primary);display:flex;align-items:center;justify-content:center;flex:none;}
.sn-tab-mark > i{width:7px;height:7px;border-radius:50%;background:#fff;font-size:0;}
.sn-tab-av{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:10px;font-weight:700;flex:none;}
.sn-tab-name{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;}
.sn-tab-dot{width:8px;height:8px;border-radius:50%;flex:none;}
/* ---- people → facepile (styling ported from the people-selector handoff;
   layout + logic unchanged — views stay left, keyline, people right) ----
   Person tabs are a plain soft-tinted circle (no ring); the selected person
   expands into a quiet pill showing the name; the name appears on hover/focus. */
.sn-tab.person{align-self:center;gap:9px;padding:5px;margin-bottom:4px;border:none;border-radius:999px;}
.sn-tab.person:hover:not(.active){background:none;}
.sn-tab.person .sn-tab-av{width:32px;height:32px;font-size:12px;transition:transform .12s ease;}
.sn-tab.person:hover:not(.active) .sn-tab-av,
.sn-tab.person:focus-visible .sn-tab-av{transform:translateY(-2px);}
.sn-tab.person .sn-tab-name{display:none;}
.sn-tab.person.active{background:#eef1f3;padding:4px 16px 4px 4px;box-shadow:none;}
.sn-tab.person.active .sn-tab-name{display:inline;font-weight:600;color:#243038;}
/* name tooltip on hover/focus for the avatar-only (unselected) people */
.sn-tab.person[data-tip]:hover::after,
.sn-tab.person[data-tip]:focus-visible::after{
  content:attr(data-tip);position:absolute;left:50%;top:-28px;transform:translateX(-50%);
  background:#243038;color:#fff;font-size:12px;font-weight:500;line-height:1;
  padding:5px 9px;border-radius:6px;white-space:nowrap;pointer-events:none;z-index:6;}

/* responsive name-hide — progressive, container-query driven so the strip always
   fits (shrink/collapse, never scroll). Names truncate, then drop to avatar-only. */
@container (max-width: 860px){ .sn-tab.person .sn-tab-name{max-width:84px;} }
@container (max-width: 720px){ .sn-tab.person .sn-tab-name{max-width:56px;} }
@container (max-width: 600px){
  .sn-tab.person .sn-tab-name{display:none;}
  .sn-tab.person{padding-left:9px;padding-right:9px;}
}
@container (max-width: 460px){
  /* very tight: drop the view-tab text labels too, keep their glyphs, and drop
     the per-person status dot + tighten avatars so more tabs fit before clipping */
  .sn-tab.view .sn-tab-vlabel{display:none;}
  .sn-tab.view{padding-left:11px;padding-right:11px;}
  .sn-tab.person .sn-tab-dot{display:none;}
  .sn-tab.person{padding-left:7px;padding-right:7px;gap:0;}
}

/* segmented control — reused by the Tickets-tab "Needs attention now" filter */
.sn-seg{display:inline-flex;gap:2px;background:#eef1f2;border:1px solid #e2e6e8;border-radius:9px;padding:3px;flex:none;}
.sn-segbtn{display:flex;align-items:center;gap:7px;padding:6px 13px;border-radius:7px;cursor:pointer;background:none;border:none;font-family:inherit;font-size:13px;font-weight:500;color:#6c777c;transition:all .12s ease;}
.sn-segbtn:hover{color:#37474f;}
.sn-segbtn.active{background:#fff;color:#15252b;box-shadow:0 1px 2px rgba(0,0,0,.08);}

/* ---- inline dropdown (priority / status pills become menus) ---- */
.sn-dd{position:relative;display:inline-flex;flex:none;}
.sn-dd-trigger{display:inline-flex;align-items:center;gap:5px;cursor:pointer;font-family:inherit;border:none;background:none;padding:0;}
.sn-dd-trigger:disabled{cursor:default;opacity:.7;}
.sn-dd-trigger .ti{font-size:12px;line-height:1;opacity:.55;}
.sn-dd-menu{position:absolute;z-index:30;top:calc(100% + 5px);right:0;min-width:172px;max-height:280px;overflow-y:auto;
  background:#fff;border:1px solid #d9dee0;border-radius:8px;box-shadow:0 6px 20px rgba(15,30,40,.14);padding:5px;}
.sn-dd-menu.left{right:auto;left:0;}
.sn-dd-opt{display:flex;align-items:center;gap:8px;width:100%;text-align:left;background:none;border:none;cursor:pointer;
  font-family:inherit;font-size:13px;color:#1f2d33;padding:7px 9px;border-radius:6px;}
.sn-dd-opt:hover,.sn-dd-opt.hl{background:#f0f4f5;}
.sn-dd-opt.sel{color:#0a6e72;font-weight:600;}
.sn-dd-opt .ti{font-size:14px;color:var(--color-primary);}
.sn-dd-opt-sub{font-family:var(--font-mono);font-size:11px;color:#90a0a6;margin-left:auto;}
.sn-dd-empty{font-size:12.5px;color:#90a0a6;padding:9px 10px;}
.sn-dd-busy{display:inline-flex;align-items:center;}
.sn-dd-busy .ti{animation:sn-spin .8s linear infinite;opacity:.6;}
@keyframes sn-spin{to{transform:rotate(360deg);}}

/* status palette */
.dot-red{background:#d64550;} .dot-amber{background:#e3a52e;} .dot-green{background:#2f9e6b;}
.dot-gray{background:#b8c2c7;}

/* ---- person banner ---- */
.sn-banner{display:flex;align-items:center;gap:14px;margin-bottom:18px;}
.sn-banner-av{width:42px;height:42px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:15px;font-weight:700;flex:none;}
.sn-banner-name{font-size:19px;font-weight:700;color:#15252b;letter-spacing:-0.01em;}
.sn-banner-dot{width:9px;height:9px;border-radius:50%;}
.sn-banner-sub{font-size:13px;color:#90a0a6;}

/* ---- cards ---- */
.sn-grid{display:grid;gap:18px;}
.sn-card{background:#fff;border:1px solid #e7e9ea;border-radius:8px;}
.sn-card.quiet{background:#fbfcfc;border:1px solid #ebedee;}
.sn-card-hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;}
.sn-eyebrow{font-family:var(--font-mono);font-size:11px;letter-spacing:.09em;text-transform:uppercase;color:#90a0a6;font-weight:500;}
.sn-eyebrow.quiet{color:#a4b0b5;}
.sn-card-title{font-size:16px;font-weight:700;color:#15252b;margin-top:3px;}
.sn-card-title.quiet{color:#5a6b72;}
.sn-progress{font-family:var(--font-mono);font-size:12px;color:#0a6e72;background:#e3f1f1;padding:3px 9px;border-radius:12px;}

/* ---- today focuses ---- */
.sn-focuses{display:flex;flex-direction:column;gap:1px;}
.sn-focus{display:flex;align-items:center;gap:12px;padding:5px 8px;border-radius:7px;transition:all .12s ease;}
.sn-focus:hover{background:#f7f8f8;}
.sn-check{width:21px;height:21px;border-radius:6px;display:flex;align-items:center;justify-content:center;cursor:pointer;flex:none;transition:all .12s ease;background:#fff;border:1.5px solid #cfd6d9;}
.sn-check.done{background:var(--color-primary);border:1.5px solid var(--color-primary);}
.sn-check > span{color:#fff;font-size:13px;line-height:1;}
.sn-check.disabled{cursor:default;}
.sn-focus-text{font-size:14.5px;flex:1;color:#1f2d33;border:none;outline:none;background:transparent;font-family:inherit;padding:0;min-width:0;}
.sn-focus-text.done{text-decoration:line-through;color:#9aa7ac;}
.sn-focus-text::placeholder{color:#b8c2c7;}
.sn-keypill{font-family:var(--font-mono);font-size:11.5px;background:#eef1f2;color:#5a6b72;padding:2px 7px;border-radius:4px;flex:none;}
.sn-rm{color:#c2cace;font-size:15px;cursor:pointer;flex:none;line-height:1;padding:0 2px;background:none;border:none;}
.sn-rm:hover{color:#d64550;}
.sn-addrow{display:flex;align-items:center;gap:12px;margin-top:10px;padding:14px 8px 0;border-top:1px solid #eef0f1;}
.sn-addbox{width:21px;height:21px;border-radius:6px;border:1.5px dashed #cfd6d9;display:flex;align-items:center;justify-content:center;color:#b8c2c7;font-size:14px;flex:none;}
.sn-addtext{flex:1;border:none;outline:none;font-size:14.5px;color:#1f2d33;background:transparent;padding:4px 0;font-family:inherit;min-width:0;}
.sn-addtext::placeholder{color:#b8c2c7;}
.sn-addkey{width:84px;border:1px solid #e2e6e8;border-radius:5px;outline:none;font-family:var(--font-mono);font-size:11.5px;color:#5a6b72;padding:5px 8px;}
/* focus → in-progress-ticket picker (combobox: dropdown of in-progress tickets
   + free-text fallback for keys not listed) */
.sn-keypick{position:relative;flex:none;}
.sn-keypick input{width:104px;}
.sn-keypick-menu{position:absolute;z-index:30;top:calc(100% + 5px);left:0;min-width:260px;max-width:340px;max-height:240px;overflow-y:auto;
  background:#fff;border:1px solid #d9dee0;border-radius:8px;box-shadow:0 6px 20px rgba(15,30,40,.14);padding:5px;}
.sn-keypick-opt{display:flex;align-items:center;gap:9px;width:100%;text-align:left;background:none;border:none;cursor:pointer;
  font-family:inherit;padding:7px 9px;border-radius:6px;}
.sn-keypick-opt:hover,.sn-keypick-opt.hl{background:#f0f4f5;}
.sn-keypick-opt .k{font-family:var(--font-mono);font-size:11.5px;color:#5a6b72;background:#eef1f2;padding:2px 7px;border-radius:4px;flex:none;}
.sn-keypick-opt .s{font-size:13px;color:#1f2d33;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sn-keypick-empty{font-size:12.5px;color:#90a0a6;padding:9px 10px;}
.sn-addbtn{background:var(--color-primary);color:#fff;border:none;border-radius:6px;padding:7px 14px;font-size:13px;font-weight:500;cursor:pointer;flex:none;transition:background .12s;}
.sn-addbtn:hover:not(:disabled){background:#00787c;}
.sn-addbtn:disabled{opacity:.6;cursor:default;}

/* ---- to raise (notes/questions) ---- */
.sn-notelist{display:flex;flex-direction:column;gap:10px;}
.sn-noterow{border:1px solid #eef0f1;border-radius:8px;padding:11px 13px;}
.sn-notetop{display:flex;align-items:center;gap:10px;}
.sn-notetext{font-size:14px;color:#1f2d33;flex:1;min-width:0;}
.sn-noteanswer{margin-top:9px;width:100%;border:1px solid #e2e6e8;border-radius:6px;outline:none;font-size:13px;color:#1f2d33;background:#fff;padding:7px 9px;font-family:inherit;}
.sn-noteanswer::placeholder{color:#b8c2c7;}
.sn-noteanswer:focus{border-color:var(--color-primary);}

/* ---- yesterday ---- */
.sn-yest{display:flex;flex-direction:column;gap:2px;}
.sn-yrow{display:flex;align-items:center;gap:11px;padding:7px 4px;}
.sn-ybox{width:17px;height:17px;border-radius:5px;display:flex;align-items:center;justify-content:center;flex:none;background:#fff;border:1.5px solid #d6dcde;}
.sn-ybox.done{background:#9fbfc0;border:none;}
.sn-ybox > span{color:#fff;font-size:11px;line-height:1;}
.sn-ytext{font-size:13.5px;flex:1;color:#9aa7ac;}
.sn-ytext.done{color:#78878d;}
.sn-ykey{font-family:var(--font-mono);font-size:11px;color:#a4b0b5;flex:none;}
.sn-recap{font-size:13.5px;line-height:1.5;color:#5a6b72;margin-bottom:12px;}

/* ---- notable tickets ---- */
.sn-grp{display:flex;align-items:center;gap:8px;margin-bottom:9px;}
.sn-grp.disc{cursor:pointer;user-select:none;}
.sn-grp-dot{width:8px;height:8px;border-radius:50%;}
.sn-grp-label{font-size:12.5px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;}
.sn-grp-count{font-family:var(--font-mono);font-size:11px;padding:1px 7px;border-radius:10px;}
.sn-grp-chev{color:#a4b0b5;font-size:12px;}
.sn-tickets{display:flex;flex-direction:column;gap:8px;}
.sn-showmore{align-self:flex-start;background:none;border:none;cursor:pointer;font-family:var(--font-mono);font-size:11.5px;letter-spacing:.03em;color:#0a6e72;padding:4px 2px;}
.sn-showmore:hover{color:var(--color-primary);text-decoration:underline;}
.sn-ticket{display:flex;align-items:center;gap:12px;padding:11px 13px;border-radius:7px;text-decoration:none;}
.sn-tkey{font-family:var(--font-mono);font-size:12px;padding:2px 8px;border-radius:4px;flex:none;text-decoration:none;cursor:pointer;}
a.sn-tkey:hover{text-decoration:underline;}
.sn-tsum{font-size:14px;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sn-tcust{font-size:11px;padding:2px 9px;border-radius:11px;flex:none;max-width:150px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:#e9f1f1;color:#4a6b6d;}
.sn-tpill{font-family:var(--font-mono);font-size:10.5px;letter-spacing:.04em;padding:3px 9px;border-radius:11px;flex:none;text-transform:uppercase;}
/* priority badge — neutral by default, escalating for High/Highest */
.sn-tprio{font-size:10.5px;font-weight:600;padding:2px 8px;border-radius:11px;flex:none;background:#eef1f2;color:#55636b;}
.sn-tprio.prio-highest{background:#fdeced;color:#a13a40;}
.sn-tprio.prio-high{background:#fbe7d6;color:#9a5512;}
.sn-tprio.prio-low,.sn-tprio.prio-lowest{background:#eef1f2;color:#8a969c;}
/* blocked */
.sn-ticket.bl{background:#fdeced;border:1px solid #f3d2d5;}
.sn-ticket.bl .sn-tsum{color:#7a2e33;}
.sn-ticket.bl .sn-tkey{background:#f4cdd0;color:#a13a40;}
.sn-ticket.bl .sn-tpill{background:#f7d6d8;color:#a13a40;}
/* progress */
.sn-ticket.ip{background:#fff;border:1px solid #ebedee;}
.sn-ticket.ip .sn-tsum{color:#37474f;}
.sn-ticket.ip .sn-tkey{background:#eef1f2;color:#5a6b72;}
.sn-ticket.ip .sn-tpill{background:#fbf2e0;color:#9a6c12;}
/* done */
.sn-ticket.dn{background:#fafbfb;border:1px solid #eef0f1;}
.sn-ticket.dn .sn-tsum{color:#97a4aa;}
.sn-ticket.dn .sn-tkey{background:#f0f2f3;color:#9aa7ac;}
.sn-ticket.dn .sn-tpill{background:#eaf2ee;color:#4c8068;}
/* most-important cards (design_handoff Option 1A) */
.sn-tcards{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:16px;}
@media(max-width:820px){.sn-tcards{grid-template-columns:1fr;}}
.sn-tcard{position:relative;display:flex;flex-direction:column;min-height:196px;padding:16px 16px 15px;background:#fff;border:1px solid #e7eae9;border-left:3px solid #e0a636;border-radius:14px;box-shadow:0 1px 2px rgba(20,40,38,.04);text-decoration:none;color:inherit;transition:box-shadow .12s,transform .12s;}
.sn-tcard:hover{box-shadow:0 4px 12px rgba(20,40,38,.09);transform:translateY(-1px);}
.sn-tcard-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:13px;}
.sn-tcard-rankid{display:flex;align-items:center;gap:9px;}
.sn-tcard-rank{display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:7px;background:#eef1f0;color:#6b7a77;font-size:12px;font-weight:800;}
.sn-tcard-rank.lead{background:#1f9d8e;color:#fff;}
.sn-tcard-id{font-family:var(--font-mono);font-size:11.5px;font-weight:600;color:#8b9693;letter-spacing:.02em;}
.sn-tcard-title{font-size:15px;line-height:1.35;letter-spacing:-.01em;color:#1c2624;font-weight:600;flex:1;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;}
.sn-tcard-barlbl{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;font-size:9.5px;font-weight:600;letter-spacing:.09em;text-transform:uppercase;color:#a2adaa;}
.sn-tcard-track{height:5px;border-radius:3px;background:#eef1f0;overflow:hidden;}
.sn-tcard-foot{display:flex;align-items:center;gap:7px;margin-top:14px;}
.sn-tcard-status{display:inline-flex;align-items:center;gap:5px;padding:4px 9px;border-radius:20px;font-size:10.5px;font-weight:700;letter-spacing:.02em;}
.sn-tcard-co{margin-left:auto;font-size:11px;font-weight:500;color:#96a29f;max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}

/* ---- brain tab ---- */
.sn-brief-list{display:flex;flex-direction:column;gap:13px;}
.sn-brief-row{display:flex;gap:11px;align-items:flex-start;}
.sn-brief-dot{width:5px;height:5px;border-radius:50%;background:var(--color-primary);margin-top:8px;flex:none;}
.sn-brief-text{font-size:14.5px;color:#37474f;line-height:1.5;}
.sn-wsub{font-size:13px;color:#90a0a6;margin-top:4px;margin-bottom:14px;}
.sn-wgrid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.sn-wtile{border:1px solid #eef0f1;border-radius:8px;padding:13px 14px;}
.sn-wtile-top{display:flex;align-items:center;gap:8px;}
.sn-wtile-dot{width:9px;height:9px;border-radius:50%;flex:none;}
.sn-wtile-count{font-family:var(--font-mono);font-size:24px;font-weight:500;color:#15252b;line-height:1;}
.sn-wtile-label{font-size:12.5px;color:#78878d;margin-top:7px;}
.sn-count-amber{font-family:var(--font-mono);font-size:12px;color:#9a6c12;background:#fbf2e0;padding:3px 9px;border-radius:12px;}
.sn-count-teal{font-family:var(--font-mono);font-size:12px;color:#0a6e72;background:#e3f1f1;padding:3px 9px;border-radius:12px;}
.sn-qlist{display:flex;flex-direction:column;gap:12px;}
.sn-qcard{border:1px solid #eef0f1;border-radius:8px;padding:14px 15px;}
.sn-qtext{font-size:14.5px;color:#1f2d33;line-height:1.45;}
.sn-qctx{font-family:var(--font-mono);font-size:11.5px;color:#90a0a6;margin-top:7px;}
.sn-qactions{display:flex;align-items:center;gap:9px;margin-top:13px;}
.sn-select{border:1px solid #e2e6e8;border-radius:6px;font-size:12.5px;color:#5a6b72;padding:6px 8px;outline:none;background:#fff;font-family:inherit;}
.sn-ghost{background:#fff;border:1px solid #dfe3e5;color:#78878d;border-radius:6px;padding:6px 12px;font-size:12.5px;font-weight:500;cursor:pointer;}
.sn-ghost:hover:not(:disabled){background:#f5f6f7;}
.sn-solid{background:var(--color-primary);border:none;color:#fff;border-radius:6px;padding:6px 14px;font-size:12.5px;font-weight:500;cursor:pointer;transition:background .12s;}
.sn-solid:hover:not(:disabled){background:#00787c;}
.sn-ghost:disabled,.sn-solid:disabled{opacity:.6;cursor:default;}
.sn-rtop{display:flex;align-items:center;gap:9px;margin-bottom:8px;}
.sn-rkind{font-family:var(--font-mono);font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;background:#eef1f2;color:#5a6b72;padding:2px 8px;border-radius:4px;flex:none;}
.sn-rtitle{font-size:14.5px;font-weight:500;color:#1f2d33;}
.sn-rsnip{font-size:13px;color:#56656b;line-height:1.5;}
.sn-ractions{display:flex;align-items:center;gap:9px;margin-top:13px;}
.sn-rsource{font-family:var(--font-mono);font-size:11px;color:#a4b0b5;flex:1;}
.sn-empty{text-align:center;color:#a4b0b5;font-size:13.5px;padding:26px 0;}

/* ---- contextual sidebar slot ---- */
.sn-sb{display:flex;flex-direction:column;height:100%;}
.sn-sb-title{font-weight:700;font-size:19px;color:#15252b;letter-spacing:-0.01em;}
.sn-sb-sub{font-size:12.5px;color:#90a0a6;margin-top:2px;}
.sn-sb-lbl{font-family:var(--font-mono);font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:#a4b0b5;font-weight:500;margin:22px 0 8px;}
.sn-sb-item{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:7px;color:#56656b;font-size:13.5px;cursor:pointer;background:none;border:none;width:100%;text-align:left;font-family:inherit;}
.sn-sb-item:hover{background:#f0f2f3;}
.sn-sb-item.active{background:#e3f1f1;color:#0a6e72;font-weight:500;}
.sn-sb-item-dot{width:7px;height:7px;border-radius:50%;background:var(--color-primary);flex:none;}
.sn-sb-queue{display:flex;align-items:center;justify-content:space-between;padding:8px 10px;border-radius:7px;color:#56656b;font-size:13.5px;cursor:pointer;background:none;border:none;width:100%;text-align:left;font-family:inherit;}
.sn-sb-queue:hover{background:#f0f2f3;}
.sn-sb-badge{font-family:var(--font-mono);font-size:11px;padding:1px 7px;border-radius:10px;}
.sn-sb-badge.amber{background:#fbf2e0;color:#9a6c12;}
.sn-sb-badge.teal{background:#e3f1f1;color:#0a6e72;}
.sn-sb-status{margin-top:auto;border-top:1px solid #ebedee;padding-top:14px;display:flex;flex-direction:column;gap:8px;}
.sn-sb-leg{display:flex;align-items:center;gap:8px;font-size:12px;color:#6c777c;}
.sn-sb-leg-dot{width:7px;height:7px;border-radius:50%;}

/* ---- urgent tab ---- */
.sn-score{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:6px;}
.sn-stat{display:flex;flex-direction:column;gap:3px;border:1px solid #ebedee;border-radius:8px;padding:11px 14px;min-width:96px;background:#fff;}
.sn-stat-count{font-family:var(--font-mono);font-size:22px;font-weight:500;line-height:1;color:#15252b;}
.sn-stat-label{font-size:11.5px;color:#78878d;}
.sn-stat-sub{font-family:var(--font-mono);font-size:10.5px;color:#90a0a6;margin-top:1px;}
.sn-stat.red{background:#fdeced;border-color:#f3d2d5;}
.sn-stat.red .sn-stat-count{color:#a13a40;}
.sn-stat.amber{background:#fbf2e0;border-color:#f0e2c2;}
.sn-stat.amber .sn-stat-count{color:#9a6c12;}
.sn-stat.green{background:#e6f4ed;border-color:#cfe8db;}
.sn-stat.green .sn-stat-count{color:#2f7d57;}
.sn-uperson{margin-bottom:7px;}
.sn-uperson-name{font-size:14px;font-weight:700;color:#15252b;margin-bottom:7px;}
.sn-uticket{display:flex;align-items:center;gap:11px;padding:9px 12px;border-radius:7px;text-decoration:none;background:#fff;border:1px solid #ebedee;}
.sn-uticket.lead{border-color:#d6dcde;background:#fbfcfc;}
.sn-uticket.t-imminent,.sn-uticket.t-breached{background:#fdeced;border-color:#f3d2d5;}
.sn-uticket.t-at-risk{background:#fbf2e0;border-color:#f0e2c2;}
.sn-uticket.t-on-track{background:#fff;border-color:#ebedee;}
.sn-ulead{color:#a13a40;font-size:13px;flex:none;line-height:1;}
.sn-usla{font-family:var(--font-mono);font-size:11px;padding:2px 9px;border-radius:11px;flex:none;white-space:nowrap;background:#eef1f2;color:#5a6b72;}
.sn-usla.red{background:#f7d6d8;color:#a13a40;}
.sn-usla.amber{background:#fbf2e0;color:#9a6c12;}
.sn-usla.green{background:#e6f4ed;color:#2f7d57;}
.sn-ustatus{font-family:var(--font-mono);font-size:10.5px;letter-spacing:.04em;text-transform:uppercase;padding:3px 9px;border-radius:11px;flex:none;background:#eef1f2;color:#5a6b72;}

/* ---- Tickets tab redesign (SLA overview, Image 9) ---- */
.sn-uhd{display:flex;align-items:flex-end;justify-content:space-between;gap:18px;margin-bottom:18px;}
.sn-uhd-title{font-size:20px;font-weight:700;color:#15252b;letter-spacing:-0.01em;}
.sn-uhd-sub{font-size:13px;color:#90a0a6;margin-top:3px;}
.sn-uhd-right{display:flex;align-items:center;gap:12px;flex:none;}
.sn-uhd-upd{font-family:var(--font-mono);font-size:11.5px;color:#90a0a6;}
.sn-refresh{display:inline-flex;align-items:center;gap:7px;background:#fff;border:1px solid #dfe3e5;color:#37474f;border-radius:7px;padding:7px 13px;font-size:13px;font-weight:500;cursor:pointer;font-family:inherit;transition:background .12s;}
.sn-refresh:hover:not(:disabled){background:#f5f6f7;}
.sn-refresh:disabled{opacity:.6;cursor:default;}
.sn-refresh .ti{font-size:15px;}
.sn-refresh.spin .ti{animation:sn-spin .8s linear infinite;}

/* four summary cards */
.sn-cards{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:18px;}
@container (max-width: 720px){ .sn-cards{grid-template-columns:repeat(2,1fr);} }
.sn-mcard{border:1px solid #ebedee;border-radius:10px;padding:15px 17px;background:#fff;}
.sn-mcard-count{font-family:var(--font-mono);font-size:30px;font-weight:500;line-height:1;color:#15252b;}
.sn-mcard-label{font-size:12.5px;color:#78878d;margin-top:8px;}
.sn-mcard.red{background:#fdeced;border-color:#f3d2d5;} .sn-mcard.red .sn-mcard-count{color:#a13a40;}
.sn-mcard.amber{background:#fbf2e0;border-color:#f0e2c2;} .sn-mcard.amber .sn-mcard-count{color:#9a6c12;}
.sn-mcard.teal{background:#e3f1f1;border-color:#cce6e6;} .sn-mcard.teal .sn-mcard-count{color:#0a6e72;}

/* needs-attention panel */
.sn-na-hd{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-bottom:14px;flex-wrap:wrap;}
.sn-na-title{font-size:16px;font-weight:700;color:#15252b;}
.sn-chips{display:flex;align-items:center;gap:7px;flex-wrap:wrap;}
.sn-pchip{display:inline-flex;align-items:center;gap:7px;padding:5px 11px 5px 6px;border-radius:18px;cursor:pointer;background:#fff;border:1px solid #e7e9ea;color:#56656b;font-size:12.5px;font-weight:500;font-family:inherit;transition:all .12s ease;}
.sn-pchip:hover{background:#f7f8f8;border-color:#dadfe1;}
.sn-pchip.sel{background:#e3f1f1;border-color:var(--color-primary);color:#0a6e72;}
.sn-pchip-av{width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:9px;font-weight:700;flex:none;}
.sn-pchip-x{font-size:13px;opacity:.6;line-height:1;}
.sn-narows{display:flex;flex-direction:column;gap:0;}
.sn-narow{border-radius:4px;background:#fff;overflow:hidden;border-bottom:1px solid #f4f6f5;}
.sn-narow:last-child{border-bottom:none;}
/* aligned column row (design_handoff "Urgent tickets") */
.sn-trow{display:grid;grid-template-columns:20px 78px 112px minmax(0,1fr) 176px 32px 100px;gap:14px;align-items:center;padding:12px;cursor:pointer;}
.sn-trow:hover{background:#fafbfb;}
.sn-trow-rank{font-size:13px;font-weight:700;color:#b3bebb;text-align:center;font-variant-numeric:tabular-nums;}
.sn-trow-id{font-family:var(--font-mono);font-size:12.5px;font-weight:600;color:#54615e;text-decoration:none;white-space:nowrap;}
.sn-trow-id:hover{text-decoration:underline;color:#1f9d8e;}
.sn-trow-main{font-size:13.5px;font-weight:500;color:#1c2624;letter-spacing:-.01em;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sn-trow-status{display:inline-flex;align-items:center;gap:6px;min-width:0;}
.sn-trow-sdot{width:6px;height:6px;border-radius:50%;flex:none;}
.sn-trow-sname{font-size:13px;font-weight:600;color:#2a3634;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sn-trow-owner{display:flex;align-items:center;justify-content:center;}
.sn-trow-end{justify-self:end;display:inline-flex;align-items:center;gap:8px;}
.sn-wchip{display:inline-flex;align-items:center;gap:6px;padding:5px 10px;border-radius:8px;font-size:12px;font-weight:700;white-space:nowrap;}
.sn-wdot{width:5px;height:5px;border-radius:50%;flex:none;}
@media(max-width:900px){
  .sn-trow{grid-template-columns:20px 74px 100px minmax(0,1fr) 32px 92px;}
  .sn-trow-status{display:none;}
}
.sn-narow.t-breached,.sn-narow.t-imminent{}
.sn-narow.t-at-risk{}
.sn-narow-main{display:flex;align-items:center;gap:11px;padding:11px 13px;cursor:pointer;}
.sn-narow-main:hover{background:#fafbfb;}
.sn-narow-dot{width:9px;height:9px;border-radius:50%;flex:none;}
.sn-narow-sum{font-size:14px;color:#1f2d33;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sn-narow-asg{display:inline-flex;align-items:center;gap:7px;flex:none;color:#56656b;font-size:12.5px;}
.sn-narow-asg-av{width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:9px;font-weight:700;flex:none;}
.sn-court{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;padding:3px 9px;border-radius:11px;flex:none;white-space:nowrap;}
.sn-court .ti{font-size:13px;}
.sn-court.ours{background:#fdeced;color:#a13a40;}
.sn-court.client{background:#eef1f2;color:#5a6b72;}
.sn-narow-link{color:#90a0a6;flex:none;display:inline-flex;}
.sn-narow-link:hover{color:var(--color-primary);}
.sn-narow-exp{border-top:1px solid #f0f3f4;padding:12px 14px;background:#fafbfb;}
/* collapsible "Why this priority" reasons */
.sn-why{margin-bottom:10px;}
.sn-why>summary{display:inline-flex;align-items:center;gap:8px;cursor:pointer;list-style:none;font-size:12.5px;font-weight:600;color:#54615e;padding:2px 0;user-select:none;}
.sn-why>summary::-webkit-details-marker{display:none;}
.sn-why>summary .ti{font-size:14px;color:#90a0a6;transition:transform .12s ease;}
.sn-why[open]>summary .ti{transform:rotate(180deg);}
.sn-why ul{margin:7px 0 0;padding-left:18px;}
.sn-why li{font-size:12.5px;color:#465562;margin:2px 0;}
.sn-narow-desc{font-size:13px;color:#56656b;line-height:1.55;margin-bottom:10px;}
.sn-narow-jira{display:inline-flex;align-items:center;gap:6px;font-family:var(--font-mono);font-size:12px;color:#0a6e72;text-decoration:none;}
.sn-narow-jira:hover{text-decoration:underline;}
/* header buttons (design_handoff): outline default, teal primary */
.sn-tbtn{display:inline-flex;align-items:center;gap:6px;border:1px solid #dfe4e2;background:#fff;color:#3a4744;font-family:inherit;font-size:13px;font-weight:600;padding:9px 14px;border-radius:10px;cursor:pointer;transition:background .12s,border-color .12s;}
.sn-tbtn:hover{background:#f6f7f7;border-color:#cfd6d3;}
.sn-tbtn.active{background:#e2f1ee;border-color:#9fd3c9;color:#177e72;}
.sn-tbtn.pri{background:#1f9d8e;border-color:#1f9d8e;color:#fff;}
.sn-tbtn.pri:hover{background:#177e72;border-color:#177e72;}
.sn-tbtn:disabled{opacity:.6;cursor:default;}
.sn-tbtn.spin .ti{animation:sn-spin 1s linear infinite;}
@keyframes sn-spin{to{transform:rotate(360deg);}}
/* severity summary bar (design_handoff) */
.sn-sevbar{display:flex;align-items:center;gap:26px;flex-wrap:wrap;background:#fff;border:1px solid #e6e9e8;border-radius:16px;padding:18px 22px;margin-bottom:20px;box-shadow:0 1px 2px rgba(20,40,38,.04);}
.sn-sevstat{display:flex;align-items:center;gap:11px;}
.sn-sevcount{font-size:26px;font-weight:800;line-height:1;font-variant-numeric:tabular-nums;}
.sn-sevlbl{font-size:12px;font-weight:700;line-height:1;}
.sn-sevsub{font-size:11px;font-weight:500;color:#9aa5a2;margin-top:3px;line-height:1;}
.sn-sevtrackwrap{flex:1;display:flex;align-items:center;gap:14px;min-width:220px;}
.sn-sevtrack{flex:1;height:8px;border-radius:5px;overflow:hidden;display:flex;background:#eef1f0;}
.sn-sevopen{font-family:var(--font-mono);font-size:12px;font-weight:600;color:#9aa5a2;white-space:nowrap;}
/* aligned column header above the rows */
.sn-tcolhd{display:grid;grid-template-columns:20px 78px 112px minmax(0,1fr) 176px 32px 100px;gap:14px;align-items:center;padding:0 12px 8px;border-bottom:1px solid #f0f2f1;margin-bottom:2px;}
.sn-tcolhd span{font-size:10px;font-weight:600;letter-spacing:.09em;color:#aab4b1;text-transform:uppercase;}
.sn-tcolhd .end{text-align:right;}
@media(max-width:900px){
  .sn-tcolhd{grid-template-columns:20px 74px 100px minmax(0,1fr) 32px 92px;}
  .sn-tcolhd .col-status{display:none;}
}
.sn-ufoot{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-top:16px;padding-top:14px;border-top:1px solid #eef0f1;flex-wrap:wrap;}
.sn-ufoot-txt{font-size:13px;color:#78878d;}
.sn-ufoot-txt b{font-family:var(--font-mono);font-weight:500;color:#5a6b72;}
.sn-viewall{display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:500;color:#0a6e72;text-decoration:none;}
.sn-viewall:hover{text-decoration:underline;}
`;

export function StandupStyles() {
  return <style>{SN_CSS}</style>;
}


// ---------------------------------------------------------------------------
// Inline dropdown menu — keyboard-accessible, closes on outside-click / Esc.
// Generic: caller supplies a trigger node + options. Used by the priority and
// status pills on every ticket-row surface. Built from page-local kt-style CSS.
// ---------------------------------------------------------------------------

interface DropdownOption {
  id: string;
  label: string;
  sub?: string;       // right-aligned mono hint (e.g. → target status)
  selected?: boolean;
}

function Dropdown({
  trigger, options, onSelect, onOpen, loading, emptyText, align = "right", ariaLabel,
}: {
  trigger: (open: boolean) => React.ReactNode;
  options: DropdownOption[];
  onSelect: (id: string) => void;
  onOpen?: () => void;        // fired once per open (lazy-fetch hook)
  loading?: boolean;
  emptyText?: string;
  align?: "left" | "right";
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    onOpen?.();
    setHl(Math.max(0, options.findIndex((o) => o.selected)));
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (!options.length) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setHl((i) => (i + 1) % options.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setHl((i) => (i - 1 + options.length) % options.length); }
      else if (e.key === "Enter") { e.preventDefault(); const o = options[hl]; if (o) { onSelect(o.id); setOpen(false); } }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, options, hl, onSelect, onOpen]);

  return (
    <div className="sn-dd" ref={ref} onClick={(e) => e.preventDefault()}>
      <button
        type="button" className="sn-dd-trigger" aria-haspopup="listbox" aria-expanded={open}
        aria-label={ariaLabel} disabled={loading}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
      >
        {trigger(open)}
        {loading
          ? <span className="sn-dd-busy"><i className="ti ti-loader-2" /></span>
          : <i className="ti ti-chevron-down" />}
      </button>
      {open && (
        <div className={`sn-dd-menu${align === "left" ? " left" : ""}`} role="listbox" aria-label={ariaLabel}>
          {loading && options.length === 0 && <div className="sn-dd-empty">Loading…</div>}
          {!loading && options.length === 0 && <div className="sn-dd-empty">{emptyText ?? "No options."}</div>}
          {options.map((o, i) => (
            <button
              key={o.id} type="button" role="option" aria-selected={o.selected}
              className={`sn-dd-opt${o.selected ? " sel" : ""}${i === hl ? " hl" : ""}`}
              onMouseEnter={() => setHl(i)}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelect(o.id); setOpen(false); }}
            >
              {o.selected && <i className="ti ti-check" />}
              <span style={{ flex: o.sub ? undefined : 1 }}>{o.label}</span>
              {o.sub && <span className="sn-dd-opt-sub">{o.sub}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Module-level priority cache — fetched once, shared across every row's dropdown.
let _prioritiesPromise: Promise<JiraPriority[]> | null = null;
function loadPriorities(): Promise<JiraPriority[]> {
  if (!_prioritiesPromise) {
    _prioritiesPromise = getPriorities().catch((e) => { _prioritiesPromise = null; throw e; });
  }
  return _prioritiesPromise;
}

/** Priority pill → dropdown. Options from getPriorities(); optimistic + rollback. */
function PriorityDropdown({ ticketKey, priority, align = "right", onChange }: {
  ticketKey: string;
  priority?: string;             // current priority NAME (the row only knows the name)
  align?: "left" | "right";
  onChange: (name: string) => void;  // optimistic local update on the row
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [priorities, setPriorities] = useState<JiraPriority[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchOnce = useCallback(() => {
    if (priorities.length || loading) return;
    setLoading(true);
    loadPriorities()
      .then(setPriorities)
      .catch((e) => enqueueSnackbar(e instanceof Error ? e.message : "Could not load priorities.", { variant: "error" }))
      .finally(() => setLoading(false));
  }, [priorities.length, loading, enqueueSnackbar]);

  const apply = async (priorityId: string) => {
    const opt = priorities.find((p) => p.id === priorityId);
    if (!opt || opt.name === priority) return;
    const prev = priority;
    onChange(opt.name); setBusy(true);
    try { await setTicketPriority(ticketKey, priorityId); enqueueSnackbar(`${ticketKey} priority → ${opt.name}.`, { variant: "success" }); }
    catch (e) { onChange(prev ?? ""); enqueueSnackbar(e instanceof Error ? e.message : "Could not set priority.", { variant: "error" }); }
    finally { setBusy(false); }
  };

  const cls = `sn-tprio prio-${(priority ?? "").toLowerCase().replace(/\s+/g, "-")}`;
  const options: DropdownOption[] = priorities.map((p) => ({ id: p.id, label: p.name, selected: p.name === priority }));

  return (
    <Dropdown
      align={align} ariaLabel={`Set priority for ${ticketKey}`}
      options={options} onOpen={fetchOnce} loading={loading || busy} emptyText="No priorities."
      onSelect={(id) => void apply(id)}
      trigger={() => <span className={cls}>{priority ?? "—"}</span>}
    />
  );
}

/** Status pill → dropdown. Lazy-fetches valid transitions on open; optimistic + rollback. */
function StatusDropdown({ ticketKey, status, pillClass = "sn-tpill", align = "right", onChange }: {
  ticketKey: string;
  status: string;
  pillClass?: string;            // visual class for the trigger pill (matches the row)
  align?: "left" | "right";
  onChange: (status: string) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [transitions, setTransitions] = useState<JiraTransition[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchOnce = useCallback(() => {
    if (transitions !== null || loading) return;
    setLoading(true);
    getTicketTransitions(ticketKey)
      .then(setTransitions)
      .catch((e) => { setTransitions([]); enqueueSnackbar(e instanceof Error ? e.message : "Could not load statuses.", { variant: "error" }); })
      .finally(() => setLoading(false));
  }, [transitions, loading, ticketKey, enqueueSnackbar]);

  const apply = async (transitionId: string) => {
    const tr = (transitions ?? []).find((t) => t.id === transitionId);
    if (!tr) return;
    const prev = status;
    onChange(tr.toStatus || tr.name); setBusy(true);
    try {
      const resulting = await setTicketTransition(ticketKey, transitionId);
      if (resulting) onChange(resulting);
      // moving a ticket changes which transitions are valid — drop the cache.
      setTransitions(null);
      enqueueSnackbar(`${ticketKey} → ${resulting || tr.toStatus || tr.name}.`, { variant: "success" });
    } catch (e) {
      onChange(prev); enqueueSnackbar(e instanceof Error ? e.message : "Could not change status.", { variant: "error" });
    } finally { setBusy(false); }
  };

  const options: DropdownOption[] = (transitions ?? []).map((t) => ({
    id: t.id, label: t.name, sub: t.toStatus && t.toStatus !== t.name ? `→ ${t.toStatus}` : undefined,
  }));

  return (
    <Dropdown
      align={align} ariaLabel={`Change status for ${ticketKey}`}
      options={options} onOpen={fetchOnce} loading={loading || busy} emptyText="No transitions available."
      onSelect={(id) => void apply(id)}
      trigger={() => <span className={pillClass}>{status}</span>}
    />
  );
}

// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------

/** Jira priority name → rank for sorting (higher = more urgent, at the top). */
const PRIORITY_RANK: Record<string, number> = { highest: 5, high: 4, medium: 3, low: 2, lowest: 1 };
const byPriority = (a: StandupTicket, b: StandupTicket) =>
  (PRIORITY_RANK[b.priority?.toLowerCase() ?? ""] ?? 0) - (PRIORITY_RANK[a.priority?.toLowerCase() ?? ""] ?? 0);

/** A ticket row on a person's tab. The priority + status pills are dropdowns
 *  that write to Jira (any signed-in user). Status/priority are kept in local
 *  state so the optimistic update lands instantly; rollback restores on error.
 *  The row is a <div> (not an <a>) so the dropdown <button>s aren't nested in an
 *  anchor (invalid HTML); the key is the Jira link. */
function TicketRow({ t, kind }: { t: StandupTicket; kind: "bl" | "ip" | "dn" }) {
  const [status, setStatus] = useState(t.status);
  const [priority, setPriority] = useState(t.priority);
  // Re-sync when the underlying poll delivers a fresh ticket.
  useEffect(() => { setStatus(t.status); }, [t.status]);
  useEffect(() => { setPriority(t.priority); }, [t.priority]);

  return (
    <div className={`sn-ticket ${kind}`}>
      {kind !== "dn" && <ScoreBadge bucket={t.bucket} score={t.score} title={t.reasons?.join(" · ")} />}
      <a className="sn-tkey" href={t.url} target="_blank" rel="noreferrer">{t.key}</a>
      <span className="sn-tsum">{t.summary}</span>
      {t.customer && <span className="sn-tcust" title={t.customer}>{t.customer}</span>}
      <PriorityDropdown ticketKey={t.key} priority={priority} onChange={setPriority} />
      <StatusDropdown ticketKey={t.key} status={status || "In progress"} pillClass="sn-tpill" onChange={setStatus} />
    </div>
  );
}

/** "Most important" priority card (design_handoff Option 1A). Status-spined
 *  card: rank chip + id + priority pill, title, priority-score bar, status pill
 *  + company footer. Whole card links to Jira. */
function TicketCard({ t, rank }: { t: StandupTicket; rank: number }) {
  const theme = statusTheme(t.status, t.blocked);
  const barPct = Math.min(100, Math.round(((t.score ?? 0) / 110) * 100));
  return (
    <a
      className="sn-tcard" href={t.url} target="_blank" rel="noreferrer"
      title={t.reasons?.join(" · ")} style={{ borderLeftColor: theme.spine }}
    >
      <div className="sn-tcard-top">
        <span className="sn-tcard-rankid">
          <span className={`sn-tcard-rank${rank === 1 ? " lead" : ""}`}>{rank}</span>
          <span className="sn-tcard-id">{t.key}</span>
        </span>
        <ScoreBadge bucket={t.bucket} score={t.score} />
      </div>
      <div className="sn-tcard-title">{t.summary}</div>
      <div style={{ marginTop: 14 }}>
        <div className="sn-tcard-barlbl">
          <span>Priority score</span>
          <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "#8b9693" }}>{Math.round(t.score ?? 0)}</span>
        </div>
        <div className="sn-tcard-track"><div style={{ width: `${barPct}%`, height: "100%", borderRadius: 3, background: theme.spine }} /></div>
      </div>
      <div className="sn-tcard-foot">
        <span className="sn-tcard-status" style={{ background: theme.bg, color: theme.fg }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: theme.spine }} />{theme.label}
        </span>
        {t.customer && <span className="sn-tcard-co" title={t.customer}>{t.customer}</span>}
      </div>
    </a>
  );
}

function TicketGroup({ label, dotClass, labelColor, count, countStyle, children, collapsible, open, onToggle }: {
  label: string;
  dotClass: string;
  labelColor: string;
  count?: number;
  countStyle?: { background: string; color: string };
  children: React.ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className={`sn-grp${collapsible ? " disc" : ""}`} onClick={collapsible ? onToggle : undefined}>
        <div className={`sn-grp-dot ${dotClass}`} />
        <span className="sn-grp-label" style={{ color: labelColor }}>{label}</span>
        {count !== undefined && countStyle && (
          <span className="sn-grp-count" style={countStyle}>{count}</span>
        )}
        {collapsible && <span className="sn-grp-chev">{open ? "▾ hide" : `▸ ${count}`}</span>}
      </div>
      {(!collapsible || open) && <div className="sn-tickets">{children}</div>}
    </div>
  );
}

const TICKET_CAP = 3;

/** Renders up to `cap` items with a "Show N more" / "Show less" toggle for the
 *  rest. Items are pre-sorted by the caller (priority/SLA order preserved). */
function CappedList<T>({ items, cap = TICKET_CAP, render }: {
  items: T[];
  cap?: number;
  render: (item: T, index: number) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, cap);
  const hidden = items.length - cap;
  return (
    <>
      {shown.map((item, i) => render(item, i))}
      {hidden > 0 && (
        <button type="button" className="sn-showmore" onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}>
          {expanded ? "Show less" : `Show ${hidden} more`}
        </button>
      )}
    </>
  );
}

/** One "to raise" note. Questions expose an optional answer box captured to the
 *  brain on raise. Mirrors the focuses-row interaction (inline, optimistic). */
function NoteRaiseRow({ note, canEdit, onRaise, onDelete }: {
  note: StandupNote;
  canEdit: boolean;
  onRaise: (id: string, answer?: string) => void;
  onDelete: (id: string) => void;
}) {
  const [answer, setAnswer] = useState("");
  const isQuestion = note.kind === "question";
  return (
    <div className="sn-noterow">
      <div className="sn-notetop">
        <span className="sn-rkind">{isQuestion ? "Q" : "Note"}</span>
        <span className="sn-notetext">{note.text}</span>
        {canEdit && (
          <>
            <button className="sn-solid" onClick={() => onRaise(note.id, isQuestion && answer.trim() ? answer.trim() : undefined)}>Raised</button>
            <button className="sn-rm" onClick={() => onDelete(note.id)} aria-label="Delete note">×</button>
          </>
        )}
      </div>
      {canEdit && isQuestion && (
        <input
          className="sn-noteanswer"
          value={answer}
          placeholder="Optional answer — captured to the brain on raise"
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && answer.trim()) onRaise(note.id, answer.trim()); }}
          aria-label="Answer (optional)"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Person tab
// ---------------------------------------------------------------------------

function PersonTab({ member, date, canEdit, emphasis, onSaved, onTickets }: {
  member: BoardMember;
  date: string;
  canEdit: boolean;
  emphasis: "balanced" | "today-forward";
  onSaved: () => void;
  onTickets: (userId: string, t: StandupTickets) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [tickets, setTickets] = useState<StandupTickets | null>(null);
  const [ticketsErr, setTicketsErr] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [showAllOpen, setShowAllOpen] = useState(false); // "see more" beyond the top-3 priority cards

  // "To raise" — this person's rolling notes/questions (author-owned, surfaced
  // under their name). Shown on every member's tab so the whole standup can see
  // them; only the owner/admin (canEdit) gets the raise/delete controls.
  const [notes, setNotes] = useState<StandupNote[]>([]);

  const [focuses, setFocuses] = useState<Focus[]>(member.focuses ?? []);
  const [newFocus, setNewFocus] = useState("");
  const dirtyRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tickets (lazy, per tab).
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setTickets(null); setTicketsErr(null);
    getStandupTickets(member.userId, ctrl.signal)
      .then((t) => { if (!cancelled) { setTickets(t); onTickets(member.userId, t); } })
      .catch((e) => { if (!cancelled) setTicketsErr(e instanceof Error ? e.message : "Failed to load tickets."); });
    return () => { cancelled = true; ctrl.abort(); };
  }, [member.userId, onTickets]);

  // Load this member's open notes (visible to everyone on their tab).
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setNotes([]);
    getStandupNotes(member.userId, ctrl.signal)
      .then((n) => { if (!cancelled) setNotes(n); })
      .catch(() => { /* notes are best-effort */ });
    return () => { cancelled = true; ctrl.abort(); };
  }, [member.userId]);

  const raiseNote = async (id: string, answer?: string) => {
    const prev = notes;
    setNotes((ns) => ns.filter((n) => n.id !== id));
    try { await raiseStandupNote(id, answer, member.userId); enqueueSnackbar(answer ? "Raised — answer captured to the brain." : "Raised.", { variant: "success" }); }
    catch (e) { setNotes(prev); enqueueSnackbar(e instanceof Error ? e.message : "Could not raise note.", { variant: "error" }); }
  };
  const dropNote = async (id: string) => {
    const prev = notes;
    setNotes((ns) => ns.filter((n) => n.id !== id));
    try { await deleteStandupNote(id, member.userId); }
    catch (e) { setNotes(prev); enqueueSnackbar(e instanceof Error ? e.message : "Could not delete note.", { variant: "error" }); }
  };

  // Reset add-focus inputs + done-collapse when switching person.
  useEffect(() => { setNewFocus(""); setShowDone(false); }, [member.userId]);

  // Re-sync focuses from the poll when there are no unsaved local edits.
  useEffect(() => { if (!dirtyRef.current) setFocuses(member.focuses ?? []); }, [member.focuses]);
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const persist = useCallback(async (next: Focus[]) => {
    try {
      const saved = await putStandupFocuses(member.userId, date, next);
      dirtyRef.current = false; setFocuses(saved); onSaved();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Could not save focuses.", { variant: "error" });
    }
  }, [member.userId, date, onSaved, enqueueSnackbar]);

  const saveNow = (next: Focus[]) => { setFocuses(next); dirtyRef.current = true; void persist(next); };
  const saveDebounced = (next: Focus[]) => {
    setFocuses(next); dirtyRef.current = true;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void persist(next), 800);
  };
  const update = (id: string, patch: Partial<Focus>, immediate: boolean) => {
    const next = focuses.map((f) => (f.id === id ? { ...f, ...patch } : f));
    immediate ? saveNow(next) : saveDebounced(next);
  };

  // Yesterday/carried ticks — optimistic display overrides (keyed `${date}:${id}`)
  // on top of the board data; the write lands on the focus's ORIGINAL row.
  const [pastDone, setPastDone] = useState<Record<string, boolean>>({});
  useEffect(() => { setPastDone({}); }, [member.userId, date]);
  const tickPast = (fDate: string, id: string, done: boolean) => {
    const k = `${fDate}:${id}`;
    setPastDone((m) => ({ ...m, [k]: done }));
    setFocusDone(member.userId, fDate, id, done).catch((e) => {
      setPastDone((m) => ({ ...m, [k]: !done }));
      enqueueSnackbar(e instanceof Error ? e.message : "Could not update focus.", { variant: "error" });
    });
  };
  // Bring an unfinished past/carried focus back into today: copy it into today's
  // focuses and close it on its original row (so it moves, not duplicates). If
  // it's already in today, just close the source.
  const bringForward = (f: Focus, fDate: string) => {
    const already = focuses.some((x) => x.text.trim().toLowerCase() === f.text.trim().toLowerCase());
    if (!already) {
      saveNow([...focuses, { id: newId(), text: f.text, done: false, ...(f.ticketKey ? { ticketKey: f.ticketKey } : {}) }]);
    }
    tickPast(fDate, f.id, true);
    enqueueSnackbar(already ? "Already in today's focuses — closed the old one." : "Brought back into today.", { variant: "success" });
  };
  const pastRow = (f: Focus, fDate: string, ageDays?: number) => {
    const isDone = pastDone[`${fDate}:${f.id}`] ?? f.done;
    return (
      <div key={`${fDate}:${f.id}`} className="sn-yrow">
        <div
          className={`sn-check${isDone ? " done" : ""}${canEdit ? "" : " disabled"}`}
          onClick={canEdit ? () => tickPast(fDate, f.id, !isDone) : undefined}
          role="checkbox" aria-checked={isDone} aria-label="Done"
        >
          {isDone && <span>✓</span>}
        </div>
        <span className={`sn-ytext${isDone ? " done" : ""}`}>{f.text}</span>
        {f.ticketKey && <span className="sn-ykey">{f.ticketKey}</span>}
        {ageDays !== undefined && (
          <span className="sn-ykey" style={{ color: "#a13a40", background: "#f7d6d8" }}>{ageDays}d ago</span>
        )}
        {canEdit && !isDone && (
          <button
            type="button" className="sn-rm" title="Bring back into today's focuses"
            aria-label="Bring back into today's focuses" onClick={() => bringForward(f, fDate)}
            style={{ marginLeft: "auto", fontSize: 15, lineHeight: 1 }}
          >
            ←
          </button>
        )}
      </div>
    );
  };
  const remove = (id: string) => saveNow(focuses.filter((f) => f.id !== id));
  const addFocus = () => {
    const text = newFocus.trim();
    if (!text) return;
    saveNow([...focuses, { id: newId(), text, done: false }]);
    setNewFocus("");
  };

  const y = member.yesterday;
  const doneCount = focuses.filter((f) => f.done).length;
  // Priority score first (server-computed), Jira priority as the tiebreak.
  const byScoreThenPriority = (a: StandupTicket, b: StandupTicket) =>
    (b.score ?? 0) - (a.score ?? 0) || byPriority(a, b);
  // Status groups show ALL of this person's assigned work — customer tickets
  // AND internal issues.
  const blocked = [...(tickets?.blocked ?? [])].sort(byScoreThenPriority);
  const active = [...(tickets?.active ?? [])].sort(byScoreThenPriority);
  const done = [...(tickets?.done ?? [])].sort(byPriority);
  // The "Most important" cards + "See more" are customer TICKETS only — internal
  // issues surface in the Blocked / In progress / Done groups below, not as
  // headline cards (Isaac, 2026-07-16).
  const openRanked = [...blocked, ...active].filter((t) => t.isTicket).sort(byScoreThenPriority);
  const topOpen = openRanked.slice(0, 3);
  const restOpen = openRanked.slice(3);
  // "Most important" cards stay score-ranked; the In progress list is grouped by
  // workflow status instead (Isaac, 2026-07-15), score-first within each status.
  const activeByStatus = [...active].sort(
    (a, b) => a.status.localeCompare(b.status) || (b.score ?? 0) - (a.score ?? 0)
  );


  const topGrid = emphasis === "today-forward" ? "1.55fr 1fr" : "1fr 1fr";

  return (
    <div>
      {/* TODAY + YESTERDAY */}
      <div className="sn-grid" style={{ gridTemplateColumns: topGrid }}>
        {/* TODAY */}
        <div className="sn-card" style={{ padding: "20px 23px" }}>
          <div className="sn-card-hd">
            <div>
              <div className="sn-eyebrow">Today</div>
              <div className="sn-card-title">Focuses</div>
            </div>
            <span className="sn-progress">{doneCount}/{focuses.length}</span>
          </div>
          <div className="sn-focuses">
            {focuses.map((f) => (
              <div key={f.id} className="sn-focus">
                <div
                  className={`sn-check${f.done ? " done" : ""}${canEdit ? "" : " disabled"}`}
                  onClick={canEdit ? () => update(f.id, { done: !f.done }, true) : undefined}
                  role="checkbox" aria-checked={f.done} aria-label="Done"
                >
                  {f.done && <span>✓</span>}
                </div>
                <input
                  className={`sn-focus-text${f.done ? " done" : ""}`}
                  value={f.text} disabled={!canEdit} placeholder="What are you focusing on?"
                  onChange={(e) => update(f.id, { text: e.target.value }, false)}
                  onBlur={() => { if (dirtyRef.current) void persist(focuses); }}
                />
                {canEdit && <button className="sn-rm" onClick={() => remove(f.id)} aria-label="Remove">×</button>}
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="sn-addrow">
              <div className="sn-addbox">+</div>
              <input
                className="sn-addtext" value={newFocus} placeholder="Add a focus…"
                onChange={(e) => setNewFocus(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addFocus(); }}
              />
              <button className="sn-addbtn" onClick={addFocus} disabled={!newFocus.trim()}>Add</button>
            </div>
          )}
        </div>

        {/* YESTERDAY & CARRIED OVER — tickable; the write lands on the focus's
            original row, and undone items resurface every day until dealt with. */}
        <div className="sn-card quiet" style={{ padding: "20px 23px" }}>
          <div className="sn-eyebrow quiet">Yesterday</div>
          <div className="sn-card-title quiet" style={{ marginBottom: 14 }}>Tick what got done</div>
          <div className="sn-yest">
            {(y?.focuses ?? []).map((f) => pastRow(f, y!.date))}
            {(member.carried?.length ?? 0) > 0 && (
              <>
                <div className="sn-eyebrow quiet" style={{ margin: "12px 0 4px" }}>Carried over — still not done</div>
                {member.carried!.map((f) =>
                  pastRow(f, f.date, Math.max(1, Math.round((Date.parse(date) - Date.parse(f.date)) / 86_400_000)))
                )}
              </>
            )}
            {(!y || y.focuses.length === 0) && (member.carried?.length ?? 0) === 0 && (
              <div className="sn-ytext" style={{ padding: "7px 4px" }}>Nothing logged yesterday.</div>
            )}
          </div>
        </div>
      </div>

      {/* TO RAISE — this person's rolling notes/questions (visible to everyone) */}
      {notes.length > 0 && (
        <div className="sn-card" style={{ marginTop: 18, padding: "20px 23px" }}>
          <div className="sn-card-hd">
            <div>
              <div className="sn-eyebrow">For this standup</div>
              <div className="sn-card-title">To raise</div>
            </div>
            <span className="sn-progress">{notes.length}</span>
          </div>
          <div className="sn-notelist">
            {notes.map((n) => (
              <NoteRaiseRow key={n.id} note={n} canEdit={canEdit} onRaise={(id, a) => void raiseNote(id, a)} onDelete={(id) => void dropNote(id)} />
            ))}
          </div>
        </div>
      )}

      {/* TICKETS — most-important cards first, then the rest, then by status. */}
      <div className="sn-card" style={{ marginTop: 18, padding: "20px 23px" }}>
        <div className="sn-card-hd">
          <div className="sn-card-title" style={{ marginTop: 0 }}>Tickets</div>
          <span className="sn-eyebrow">JIRA</span>
        </div>
        {!tickets && !ticketsErr && <Spinner />}
        {ticketsErr && <div style={{ color: "#a13a40", fontSize: 14 }}>{ticketsErr}</div>}
        {tickets?.unmapped && (
          <div className="sn-banner-sub">Not mapped to Jira. Map in <a href="/settings" style={{ color: "#0a6e72" }}>Settings → Jira user mapping</a>.</div>
        )}
        {tickets && !tickets.unmapped && (
          <>
            {/* MOST IMPORTANT — top 3 open by priority score, as cards. */}
            {topOpen.length > 0 && (
              <>
                <div className="sn-grp"><div className="sn-grp-dot dot-red" /><span className="sn-grp-label">Most important</span></div>
                <div className="sn-tcards">
                  {topOpen.map((t, i) => <TicketCard key={t.key} t={t} rank={i + 1} />)}
                </div>
                {restOpen.length > 0 && (
                  <>
                    <button type="button" className="sn-showmore" aria-expanded={showAllOpen}
                      onClick={() => setShowAllOpen((v) => !v)} style={{ marginBottom: showAllOpen ? 8 : 14 }}>
                      {showAllOpen ? "Show less" : `See ${restOpen.length} more`}
                    </button>
                    {showAllOpen && (
                      <div className="sn-tickets" style={{ marginBottom: 14 }}>
                        {restOpen.map((t) => <TicketRow key={t.key} t={t} kind={t.blocked ? "bl" : "ip"} />)}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {blocked.length > 0 && (
              <TicketGroup label="Blocked" dotClass="dot-red" labelColor="#a13a40"
                count={blocked.length} countStyle={{ background: "#f7d6d8", color: "#a13a40" }}>
                <CappedList items={blocked} render={(t) => <TicketRow key={t.key} t={t} kind="bl" />} />
              </TicketGroup>
            )}
            {active.length > 0 && (
              <TicketGroup label="In progress" dotClass="dot-amber" labelColor="#9a6c12">
                <CappedList items={activeByStatus} render={(t) => <TicketRow key={t.key} t={t} kind="ip" />} />
              </TicketGroup>
            )}
            {done.length > 0 && (
              <TicketGroup label="Done" dotClass="dot-green" labelColor="#4c8068"
                count={done.length} collapsible open={showDone} onToggle={() => setShowDone((v) => !v)}>
                {done.map((t) => <TicketRow key={t.key} t={t} kind="dn" />)}
              </TicketGroup>
            )}
            {!blocked.length && !active.length && !done.length && (
              <div className="sn-banner-sub">No tickets.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brain tab
// ---------------------------------------------------------------------------

const SCOPE_OPTIONS = ["company", "personal"];

function QuestionCard({ q, canEdit, onResolved }: { q: StandupQuestion; canEdit: boolean; onResolved: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const [scope, setScope] = useState("company");
  const [busy, setBusy] = useState(false);

  const onAnswer = async () => {
    setBusy(true);
    try { await answerQuestion(q.question_id, q.text, scope); enqueueSnackbar("Answer captured.", { variant: "success" }); onResolved(); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Could not answer.", { variant: "error" }); setBusy(false); }
  };
  const onDismiss = async () => {
    setBusy(true);
    try { await dismissQuestion(q.question_id); enqueueSnackbar("Dismissed.", { variant: "success" }); onResolved(); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Could not dismiss.", { variant: "error" }); setBusy(false); }
  };

  return (
    <div className="sn-qcard">
      <div className="sn-qtext">{q.text}</div>
      {q.kind && <div className="sn-qctx">{q.kind}</div>}
      {canEdit && (
        <div className="sn-qactions">
          <select className="sn-select" value={scope} onChange={(e) => setScope(e.target.value)}>
            {SCOPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          <button className="sn-ghost" disabled={busy} onClick={() => void onDismiss()}>Dismiss</button>
          <button className="sn-solid" disabled={busy} onClick={() => void onAnswer()}>Answer</button>
        </div>
      )}
    </div>
  );
}

function ReviewCard({ item, canEdit, onResolved }: { item: PendingItem; canEdit: boolean; onResolved: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);

  // PendingItem has no kind/title/snippet/source split — derive from the fields
  // we have (text → title + snippet, target_scope → kind, proposed_by → source).
  const lines = item.text.split("\n").map((l) => l.trim()).filter(Boolean);
  const title = lines[0] ?? item.text;
  const snippet = lines.slice(1).join(" ") || (lines.length <= 1 && title !== item.text ? item.text : "");
  const kind = (item.target_scope || "memory").split(":")[0];
  const source = `from ${item.proposed_by}${item.proposed_via ? ` · ${item.proposed_via}` : ""}`;

  const onApprove = async () => {
    setBusy(true);
    try { await approveMemory(item.item_id, item.target_scope); enqueueSnackbar("Approved.", { variant: "success" }); onResolved(); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Could not approve.", { variant: "error" }); setBusy(false); }
  };
  const onReject = async () => {
    setBusy(true);
    try { await rejectMemory(item.item_id, item.target_scope); enqueueSnackbar("Rejected.", { variant: "success" }); onResolved(); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Could not reject.", { variant: "error" }); setBusy(false); }
  };

  return (
    <div className="sn-qcard">
      <div className="sn-rtop">
        <span className="sn-rkind">{kind}</span>
        <span className="sn-rtitle">{title}</span>
      </div>
      {snippet && <div className="sn-rsnip">{snippet}</div>}
      <div className="sn-ractions">
        <span className="sn-rsource">{source}</span>
        {canEdit && (
          <>
            <button className="sn-ghost" disabled={busy} onClick={() => void onReject()}>Reject</button>
            <button className="sn-solid" disabled={busy} onClick={() => void onApprove()}>Approve</button>
          </>
        )}
      </div>
    </div>
  );
}

const WEATHER_META: { key: Weather; label: string; dotClass: string }[] = [
  { key: "red", label: "Need attention", dotClass: "dot-red" },
  { key: "amber", label: "Watch", dotClass: "dot-amber" },
  { key: "green", label: "Healthy", dotClass: "dot-green" },
  { key: "grey", label: "Stale / unknown", dotClass: "dot-gray" },
];

/** Build the morning-brief sentence list from the StandupBrief fields. */
function briefSentences(brief: StandupBrief | null): string[] {
  if (!brief) return [];
  const out: string[] = [];
  if (brief.health) out.push(brief.health);
  if (brief.questions.length) out.push(`${brief.questions.length} open question${brief.questions.length === 1 ? "" : "s"} the brain is unsure about.`);
  if (brief.stale_reviews) out.push(`${brief.stale_reviews} review${brief.stale_reviews === 1 ? "" : "s"} have been waiting more than 48h.`);
  if (brief.oldest_pending_age_h) out.push(`Oldest pending memory is ${Math.round(brief.oldest_pending_age_h)}h old.`);
  if (!out.length) out.push("The knowledge base is quiet this morning — nothing flagged.");
  return out;
}

function BrainTab({ canEdit, onCounts }: { canEdit: boolean; onCounts?: (q: number, r: number) => void }) {
  const [brief, setBrief] = useState<StandupBrief | null>(null);
  const [weather, setWeather] = useState<Record<Weather, number> | null>(null);
  const [weatherTotal, setWeatherTotal] = useState(0);
  const [questions, setQuestions] = useState<StandupQuestion[]>([]);
  const [review, setReview] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getStandupBrief().catch(() => null),
      getWeather()
        .then((w) => ({ counts: { green: w.green.length, amber: w.amber.length, red: w.red.length, grey: w.grey.length } as Record<Weather, number> }))
        .catch(() => null),
      reviewPending().catch(() => [] as PendingItem[]),
    ]).then(([b, w, r]) => {
      setBrief(b);
      setQuestions(b?.questions ?? []);
      if (w) {
        setWeather(w.counts);
        setWeatherTotal(w.counts.green + w.counts.amber + w.counts.red + w.counts.grey);
      }
      setReview(r);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  useEffect(() => { onCounts?.(questions.length, review.length); }, [questions.length, review.length, onCounts]);

  const resolveQuestion = (id: string) => setQuestions((qs) => qs.filter((q) => q.question_id !== id));
  const resolveReview = (id: string) => setReview((rs) => rs.filter((r) => r.item_id !== id));

  if (loading) return <Spinner />;

  const sentences = briefSentences(brief);

  return (
    <div>
      {/* Row 1: brief + weather */}
      <div className="sn-grid" style={{ gridTemplateColumns: "1.45fr 1fr" }}>
        <div className="sn-card" style={{ padding: "20px 23px" }}>
          <div className="sn-eyebrow">Morning brief</div>
          <div className="sn-card-title" style={{ marginBottom: 14 }}>What the brain knows this morning</div>
          <div className="sn-brief-list">
            {sentences.map((s, i) => (
              <div key={i} className="sn-brief-row"><div className="sn-brief-dot" /><span className="sn-brief-text">{s}</span></div>
            ))}
          </div>
        </div>
        <div className="sn-card" style={{ padding: "20px 23px" }}>
          <div className="sn-eyebrow">Knowledge weather</div>
          <div className="sn-wsub">{weatherTotal} tracked entities</div>
          <div className="sn-wgrid">
            {WEATHER_META.map((m) => (
              <div key={m.key} className="sn-wtile">
                <div className="sn-wtile-top">
                  <div className={`sn-wtile-dot ${m.dotClass}`} />
                  <span className="sn-wtile-count">{weather?.[m.key] ?? 0}</span>
                </div>
                <div className="sn-wtile-label">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: questions + review */}
      <div className="sn-grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 18 }}>
        <div className="sn-card" style={{ padding: "20px 23px" }}>
          <div className="sn-card-hd">
            <div>
              <div className="sn-eyebrow">Open questions</div>
              <div className="sn-card-title">Things the brain is unsure about</div>
            </div>
            <span className="sn-count-amber">{questions.length}</span>
          </div>
          <div className="sn-qlist">
            {questions.map((q) => <QuestionCard key={q.question_id} q={q} canEdit={canEdit} onResolved={() => resolveQuestion(q.question_id)} />)}
            {questions.length === 0 && <div className="sn-empty">All questions resolved ✓</div>}
          </div>
        </div>

        <div className="sn-card" style={{ padding: "20px 23px" }}>
          <div className="sn-card-hd">
            <div>
              <div className="sn-eyebrow">Review queue</div>
              <div className="sn-card-title">Memories awaiting approval</div>
            </div>
            <span className="sn-count-teal">{review.length}</span>
          </div>
          <div className="sn-qlist">
            {review.map((item) => <ReviewCard key={item.item_id} item={item} canEdit={canEdit} onResolved={() => resolveReview(item.item_id)} />)}
            {review.length === 0 && <div className="sn-empty">Queue cleared ✓</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Urgent tickets tab (SLA board)
// ---------------------------------------------------------------------------

// ── Ticket design tokens (design_handoff_aeronet_tickets) ──────────────────
// Priority-score bucket → colours (fg / bg). Hi-fi values from the handoff.
const BUCKET_STYLE: Record<string, { bg: string; fg: string }> = {
  P1: { fg: "#c0392b", bg: "#fdeceb" },
  P2: { fg: "#b0781c", bg: "#fbf0d9" },
  P3: { fg: "#5b6b78", bg: "#eef1f3" },
};

// Status theme for the priority cards (fg / bg pill + left-spine colour).
type StatusTheme = { label: string; fg: string; bg: string; spine: string };
function statusTheme(status: string, blocked?: boolean): StatusTheme {
  const s = (status || "").toLowerCase();
  if (blocked || s.includes("block")) return { label: status || "Blocked", fg: "#c0392b", bg: "#fdeceb", spine: "#e05650" };
  if (s.includes("develop")) return { label: status || "With developers", fg: "#1f7d6e", bg: "#e2f1ee", spine: "#2fa694" };
  return { label: status || "In progress", fg: "#b0781c", bg: "#fbf0d9", spine: "#e0a636" };
}

// Status → leading dot colour on the urgent-list rows.
function statusDotColor(status: string): string {
  const s = (status || "").toLowerCase();
  if (s.includes("develop")) return "#2fa694";
  if (s.includes("selected")) return "#4f7bbf";
  if (s.includes("progress")) return "#e0a636";
  if (s.includes("block")) return "#e05650";
  if (s.includes("client") || s.includes("waiting") || s.includes("to do")) return "#9aa5a2";
  return "#9aa5a2";
}

// Wait-days → severity colours (fg / bg chip + row spine). Neutral spine < 7d.
function waitSeverity(days: number): { fg: string; bg: string; spine: string } {
  if (days >= 14) return { fg: "#c0392b", bg: "#fdeceb", spine: "#e05650" };
  if (days >= 7) return { fg: "#b0781c", bg: "#fbf0d9", spine: "#e0a636" };
  return { fg: "#1f7d6e", bg: "#e2f1ee", spine: "#e8ebea" };
}

/** Priority pill — "P2 58" (bucket + score), bucket colours, radius 7. */
function ScoreBadge({ bucket, score, title }: { bucket?: "P1" | "P2" | "P3"; score?: number; title?: string }) {
  if (!bucket || score === undefined) return null;
  const s = BUCKET_STYLE[bucket];
  return (
    <span
      title={title ?? `Priority score ${score} (${bucket})`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, flex: "none",
        background: s.bg, color: s.fg, borderRadius: 7, padding: "4px 8px",
        fontSize: 11, fontWeight: 800, letterSpacing: ".02em",
      }}
    >
      {bucket}
      <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.8, fontFamily: "var(--font-mono)" }}>{Math.round(score)}</span>
    </span>
  );
}

// tier → traffic-light bucket for SLA chip / ticket styling.
function tierTone(tier: UrgentTicket["tier"]): "red" | "amber" | "green" | "" {
  if (tier === "breached" || tier === "imminent") return "red";
  if (tier === "at-risk") return "amber";
  if (tier === "on-track") return "green";
  return "";
}

/** Human time from ms (positive, e.g. "2h 2m"). */
function fmtDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  if (d >= 1) {
    const rem = (s % 86400) / 86400;
    return `${(d + rem).toFixed(1)}d`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m`;
  if (m >= 1) return `${m}m`;
  return `${Math.max(s, 0)}s`;
}

/** Format an SlaState's remaining time → "2h 2m to breach" / "breached 3.0d". */
function fmtSla(s: SlaState): string {
  if (s.paused) return "paused";
  const r = s.remainingMs;
  if (r === undefined) return s.breached ? "breached" : "—";
  if (r < 0 || s.breached) return `breached ${fmtDuration(Math.abs(r))}`;
  return `${fmtDuration(r)} to breach`;
}

/** Pick the worst/nearest of response vs resolution. */
function worstSla(t: UrgentTicket): SlaState | null {
  const states = [t.response, t.resolution].filter((x): x is SlaState => !!x);
  if (states.length === 0) return null;
  return states.reduce((worst, cur) => {
    const score = (s: SlaState) => (s.breached ? -Infinity : s.remainingMs ?? Infinity);
    return score(cur) < score(worst) ? cur : worst;
  });
}

/** tier → status-dot class for the row's leading dot. */
function tierDotClass(tier: UrgentTicket["tier"]): string {
  const tone = tierTone(tier);
  return tone === "red" ? "dot-red" : tone === "amber" ? "dot-amber" : tone === "green" ? "dot-green" : "dot-gray";
}

/** Whether the ball is in our court. Defaults to true (our reply) when the
 *  backend hasn't supplied `inOurCourt` yet — the conservative "we owe a reply". */
function isOurCourt(t: UrgentTicket): boolean {
  return t.inOurCourt !== false;
}

/** One urgent ticket row (Image 9): status dot · key (Jira) · summary · assignee ·
 *  our-reply/with-client · time pill. Expands to the audit view: what it is,
 *  next step, waiting/deadline detail, AI draft, Jira link. Expanding marks the
 *  ticket reviewed for the day (the audit sweep's progress tick). */
function UrgentRow({ t, draft, reviewed, onOpen, rank, brief }: {
  t: UrgentTicket;
  draft?: TicketDraft;
  reviewed?: boolean;
  onOpen?: (key: string) => void;
  /** 1-based position when rendered inside Today's priorities. */
  rank?: number;
  /** The brief's reply/action call + one-line why (shown as the row line). */
  brief?: TicketWatchBriefItem;
}) {
  const [open, setOpen] = useState(false);
  const sla = worstSla(t);
  const tone = tierTone(t.tier);
  const overdue = !!sla && (sla.breached || (sla.remainingMs !== undefined && sla.remainingMs < 0));
  const ours = isOurCourt(t);
  const nzToday = new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });
  const toggle = () => setOpen((v) => { if (!v) onOpen?.(t.key); return !v; });

  // Row spine = severity: customer wait-days if awaiting, else SLA tier.
  const spine = t.awaiting
    ? waitSeverity(t.awaiting.days).spine
    : t.tier === "breached" ? "#e05650"
    : (t.tier === "imminent" || t.tier === "at-risk") ? "#e0a636"
    : "#e8ebea";

  // The single right-aligned chip: most-urgent fact (waiting > deadline > SLA),
  // severity-coloured. The rest lives in the expand.
  let chip: { fg: string; bg: string; label: string; title?: string } | null = null;
  if (t.awaiting) {
    const w = waitSeverity(t.awaiting.days);
    chip = { fg: w.fg, bg: w.bg, label: t.awaiting.days < 1 ? "<1d" : `${t.awaiting.days}d`,
      title: `Waiting since ${t.awaiting.since.slice(0, 10)}${sla ? ` · ${fmtSla(sla)}` : ""}` };
  } else if (t.deadline) {
    const red = t.deadline.date <= nzToday;
    chip = { fg: red ? "#c0392b" : "#b0781c", bg: red ? "#fdeceb" : "#fbf0d9", label: `due ${t.deadline.date.slice(5)}`, title: `"${t.deadline.quote}"` };
  } else if (sla) {
    chip = overdue
      ? { fg: "#c0392b", bg: "#fdeceb", label: fmtSla(sla) }
      : tone === "amber" ? { fg: "#b0781c", bg: "#fbf0d9", label: fmtSla(sla) }
      : { fg: "#1f7d6e", bg: "#e2f1ee", label: fmtSla(sla) };
  }

  return (
    <div className={`sn-narow t-${t.tier}`} style={{ borderLeft: `3px solid ${spine}` }}>
      <div
        className="sn-trow" role="button" tabIndex={0} aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
      >
        <span className="sn-trow-rank">{rank ?? ""}</span>
        <ScoreBadge bucket={t.bucket} score={t.score} />
        <a className="sn-trow-id" href={t.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{t.key}</a>
        <span className="sn-trow-main">{brief?.why ?? t.summary}</span>
        <span className="sn-trow-status">
          {t.status && <><span className="sn-trow-sdot" style={{ background: statusDotColor(t.status) }} /><span className="sn-trow-sname">{t.status}</span></>}
        </span>
        <span className="sn-trow-owner">
          {t.assigneeName && (
            <span className="sn-narow-asg-av" title={t.assigneeName} style={{ background: colorFor(t.assigneeAccountId ?? t.assigneeName) }}>{initials(t.assigneeName)}</span>
          )}
        </span>
        <span className="sn-trow-end">
          {reviewed && <i className="ti ti-eye-check" style={{ color: "#1f9d8e" }} aria-label="Reviewed today" title="Reviewed today" />}
          {chip && (
            <span className="sn-wchip" style={{ background: chip.bg, color: chip.fg }} title={chip.title}>
              <span className="sn-wdot" style={{ background: chip.fg }} />{chip.label}
            </span>
          )}
          <i className={`ti ti-chevron-${open ? "up" : "down"} sn-narow-link`} aria-hidden="true" />
        </span>
      </div>
      {open && (
        <div className="sn-narow-exp">
          {/* Inside the priorities card the row line is the "why" — restate the
              ticket's own summary so the expansion stands alone. */}
          {brief && <div className="sn-narow-desc"><b>Ticket:</b> {t.summary}</div>}
          {/* WHAT IT IS — nightly AI gist, else the raw description snippet. */}
          {(t.gist || t.description)
            ? <div className="sn-narow-desc"><b>What it is:</b> {t.gist ?? t.description}</div>
            : <div className="sn-narow-desc" style={{ color: "#90a0a6" }}>No description.</div>}
          {t.gist && t.description && (
            <div className="sn-narow-desc" style={{ color: "#90a0a6" }}>{t.description}</div>
          )}
          {/* NEXT STEP — the audit's answer to "what needs to happen". */}
          {t.next && (
            <div className="sn-narow-desc" style={{ display: "flex", alignItems: "baseline", gap: 6, color: "#0a6e72", fontWeight: 600 }}>
              <i className="ti ti-arrow-big-right-line" aria-hidden="true" />
              <span>Next step: {t.next}</span>
            </div>
          )}
          {/* METRICS — the facts pulled off the collapsed row, in one line. */}
          <div className="sn-narow-desc" style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", color: "#56656b" }}>
            <span style={{ color: ours ? "#a13a40" : "#2f7d57", fontWeight: 600 }}>
              {ours ? "Our reply" : "With client"}
            </span>
            {t.status && <span>Status: {t.status}</span>}
            {t.awaiting && <span>Waiting {t.awaiting.days < 1 ? "<1" : t.awaiting.days}d (since {t.awaiting.since.slice(0, 10)})</span>}
            {sla && <span>SLA: {fmtSla(sla)}</span>}
          </div>
          {t.deadline && (
            <div className="sn-narow-desc" style={{ fontStyle: "italic" }}>
              Deadline {t.deadline.date} — source: “{t.deadline.quote}”
            </div>
          )}
          {/* WHY THIS SCORE — collapsed by default; a small toggle reveals the
              tunable engine's reasons (strongest first). */}
          {(t.reasons?.length ?? 0) > 0 && (
            <details className="sn-why">
              <summary>
                <ScoreBadge bucket={t.bucket} score={t.score} />
                <span>Why this priority</span>
                <i className="ti ti-chevron-down" aria-hidden="true" />
              </summary>
              <ul>
                {t.reasons!.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </details>
          )}
          {draft && (
            <div className="sn-narow-desc" style={{ borderTop: "1px solid var(--kt-border)", paddingTop: 10, marginTop: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <i className="ti ti-sparkles" />
                <b>AI draft — review before sending</b>
                <span className="sn-usla">{draft.canAnswer} · {Math.round(draft.confidence * 100)}%</span>
                {draft.draft && (
                  <button
                    className="sn-refresh" type="button"
                    onClick={(e) => { e.stopPropagation(); void navigator.clipboard.writeText(draft.draft); }}
                  >
                    <i className="ti ti-copy" />Copy
                  </button>
                )}
              </div>
              {draft.draft
                ? <div style={{ whiteSpace: "pre-wrap" }}>{draft.draft}</div>
                : <div style={{ color: "#90a0a6" }}>Can’t answer from the brain.</div>}
              {draft.missing && <div style={{ color: "#90a0a6", marginTop: 6 }}>Missing: {draft.missing}</div>}
            </div>
          )}
          <a className="sn-narow-jira" href={t.url} target="_blank" rel="noreferrer">
            <i className="ti ti-external-link" />Open {t.key} in Jira
          </a>
        </div>
      )}
    </div>
  );
}

/** Overnight new-ticket digest (ticket-watch). Collapses per day via localStorage
 *  once dismissed, so the morning read is one click and stays out of the way. */
function OvernightCard({ date, items }: { date: string; items: TicketWatchDigestItem[] }) {
  const seenKey = `brain.tickets.overnightSeen:${date}`;
  const [open, setOpen] = useState(() => localStorage.getItem(seenKey) !== "1");
  const toggle = () =>
    setOpen((v) => {
      const nv = !v;
      if (!nv) localStorage.setItem(seenKey, "1");
      return nv;
    });
  return (
    <div className="sn-card" style={{ padding: "20px 23px", marginBottom: 14 }}>
      <div
        className="sn-na-hd" role="button" tabIndex={0} aria-expanded={open} onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
        style={{ cursor: "pointer" }}
      >
        <div className="sn-na-title">
          <i className="ti ti-sunrise" /> Overnight — {items.length} new ticket{items.length === 1 ? "" : "s"}
        </div>
        <i className={`ti ti-chevron-${open ? "up" : "down"}`} aria-hidden="true" />
      </div>
      {open && (
        <div className="sn-narows">
          {items.map((d) => (
            <div key={d.key} className="sn-narow">
              <div className="sn-narow-main">
                <a className="sn-tkey" href={d.url} target="_blank" rel="noreferrer">{d.key}</a>
                <span className="sn-narow-sum">{d.line}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** "Today's priorities" — the highest-SCORED open tickets (deterministic, so
 *  it always agrees with the P1/P2/P3 severity bar: the P1s lead). Each row
 *  borrows the nightly brief's one-line "why" when one exists, else the ticket
 *  summary. Full expandable audit view like every other section. */
function BriefCard({ tickets, briefByKey, drafts, reviewed, onOpen }: {
  tickets: UrgentTicket[];
  briefByKey: Map<string, TicketWatchBriefItem>;
  drafts?: Record<string, TicketDraft>;
  reviewed: Set<string>;
  onOpen: (key: string) => void;
}) {
  return (
    <div className="sn-card" style={{ padding: "20px 23px", marginBottom: 14 }}>
      <div className="sn-na-hd">
        <div className="sn-na-title"><i className="ti ti-list-numbers" /> Today's priorities</div>
      </div>
      <TicketColHeader />
      <div className="sn-narows">
        {tickets.map((t, n) => (
          <UrgentRow
            key={t.key} t={t} rank={n + 1} brief={briefByKey.get(t.key)} draft={drafts?.[t.key]}
            reviewed={reviewed.has(t.key)} onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  );
}

function UrgentMetricCard({ count, label, tone }: { count: number; label: string; tone?: "red" | "amber" | "teal" }) {
  return (
    <div className={`sn-mcard${tone ? ` ${tone}` : ""}`}>
      <div className="sn-mcard-count">{count}</div>
      <div className="sn-mcard-label">{label}</div>
    </div>
  );
}

/** Severity summary bar (design_handoff): P1/P2/P3 counts + a stacked
 *  proportion bar + "N open". Replaces the old metric-card row. */
function SeverityBar({ sb }: { sb: UrgentResult["scoreboard"] }) {
  const p1 = sb.p1 ?? 0, p2 = sb.p2 ?? 0, p3 = sb.p3 ?? 0;
  const total = sb.open || p1 + p2 + p3;
  const n = total || 1;
  const stats = [
    { count: p1, label: "P1 · critical", sub: "Breaching now", fg: "#c0392b" },
    { count: p2, label: "P2 · high", sub: "Watch closely", fg: "#b0781c" },
    { count: p3, label: "P3 · watch", sub: "Backlog", fg: "#5b6b78" },
  ];
  return (
    <div className="sn-sevbar">
      {stats.map((s) => (
        <div key={s.label} className="sn-sevstat">
          <span className="sn-sevcount" style={{ color: s.fg }}>{s.count}</span>
          <div>
            <div className="sn-sevlbl" style={{ color: s.fg }}>{s.label}</div>
            <div className="sn-sevsub">{s.sub}</div>
          </div>
        </div>
      ))}
      <div className="sn-sevtrackwrap">
        <div className="sn-sevtrack">
          <div style={{ width: `${(p1 / n) * 100}%`, background: "#e05650" }} />
          <div style={{ width: `${(p2 / n) * 100}%`, background: "#e0a636" }} />
          <div style={{ width: `${(p3 / n) * 100}%`, background: "#b7c2c6" }} />
        </div>
        <span className="sn-sevopen">{total} open</span>
      </div>
    </div>
  );
}

/** Aligned column header sitting above a group of urgent rows. */
function TicketColHeader() {
  return (
    <div className="sn-tcolhd">
      <span></span>
      <span>Priority</span>
      <span>Ticket</span>
      <span>Summary</span>
      <span className="col-status">Status</span>
      <span></span>
      <span className="end">Waiting</span>
    </div>
  );
}

/** "Updated Ns ago" from a fetched-at epoch. */
function fmtAgo(ts: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

type UrgentSeg = "all" | "breached" | "imminent" | "open";

// How many top-scored tickets to show in "Today's priorities".
const PRIORITY_COUNT = 6;

// Weight fields grouped for the admin editor (label → config.weights key).
const WEIGHT_GROUPS: { title: string; fields: [string, string][] }[] = [
  { title: "Biggest red flags", fields: [
    ["Major incident flag", "major_incident"],
    ["No first response to customer yet", "no_first_response"],
    ["First-response SLA breached", "sla_first_response_breach"],
    ["Resolution SLA breached", "sla_resolution_breach"],
  ]},
  { title: "Sitting / going stale", fields: [
    ["Stuck in status — points per day", "stuck_per_day"],
    ["Stuck in status — cap", "stuck_cap"],
    ["No update — points per day", "stale_per_day"],
    ["No update — cap", "stale_cap"],
    ["SLA approaching breach", "sla_approaching_breach"],
    ["Total age — points per day", "age_per_day"],
    ["Total age — cap", "age_cap"],
  ]},
  { title: "Process problems", fields: [
    ["‘With Developers’ but no linked item", "dev_no_linked_item"],
    ["No real owner (generic/unassigned)", "no_owner"],
    ["‘Selected for Dev’ not with the developer", "dev_not_aaron"],
    ["Dev-stage but no release tag", "dev_no_release"],
  ]},
  { title: "Jira priority", fields: [
    ["Highest", "priority_Highest"],
    ["High", "priority_High"],
    ["Medium", "priority_Medium"],
  ]},
];

/** Admin-only editor for the priority-scoring weights + thresholds. Loads the
 *  live config, lets an admin retune every signal, and saves it back — the
 *  server clears the urgent cache so the new weights apply on the next poll. */
function ScoringPanel({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const [cfg, setCfg] = useState<ScoringConfig | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    getScoringConfig(ctrl.signal)
      .then(setCfg)
      .catch((e) => { if (!ctrl.signal.aborted) enqueueSnackbar(e instanceof Error ? e.message : "Failed to load weights.", { variant: "error" }); });
    return () => ctrl.abort();
  }, [enqueueSnackbar]);

  const setWeight = (k: string, v: number) =>
    setCfg((c) => (c ? { ...c, weights: { ...c.weights, [k]: v } } : c));
  const setTop = (k: "bucketP1" | "bucketP2" | "waitingDampen", v: number) =>
    setCfg((c) => (c ? { ...c, [k]: v } : c));

  const save = () => {
    if (!cfg) return;
    setBusy(true);
    putScoringConfig(cfg)
      .then((saved) => { setCfg(saved); enqueueSnackbar("Scoring weights saved — applies on the next refresh.", { variant: "success" }); onSaved(); })
      .catch((e) => enqueueSnackbar(e instanceof Error ? e.message : "Failed to save weights.", { variant: "error" }))
      .finally(() => setBusy(false));
  };

  const numInput = (value: number, onChange: (v: number) => void) => (
    <input
      type="number" value={value} step="any" disabled={busy}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ width: 64, padding: "3px 6px", border: "1px solid var(--kt-border, #d9dee0)", borderRadius: 5, fontSize: 13, textAlign: "right" }}
    />
  );

  return (
    <div className="sn-card" style={{ padding: "20px 23px", marginBottom: 14 }}>
      <div className="sn-na-hd">
        <div className="sn-na-title"><i className="ti ti-adjustments" /> Priority scoring weights</div>
        <button className="sn-rm" onClick={onClose} aria-label="Close">×</button>
      </div>
      {!cfg ? <Spinner /> : (
        <>
          <p style={{ fontSize: 13, color: "#5a6b72", margin: "6px 0 14px" }}>
            Every open ticket earns these points per warning sign; the total is its score.
            Tickets parked on the customer have neglect signals cut to {Math.round(cfg.waitingDampen * 100)}%.
            Changes apply on the next refresh and re-rank every tab.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            {WEIGHT_GROUPS.map((g) => (
              <div key={g.title}>
                <div className="sn-eyebrow" style={{ marginBottom: 6 }}>{g.title}</div>
                {g.fields.map(([label, key]) => (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "3px 0" }}>
                    <span style={{ fontSize: 12.5, color: "#465562" }}>{label}</span>
                    {numInput(cfg.weights[key] ?? 0, (v) => setWeight(key, v))}
                  </div>
                ))}
              </div>
            ))}
            <div>
              <div className="sn-eyebrow" style={{ marginBottom: 6 }}>Buckets & dampening</div>
              {([["P1 threshold (≥)", "bucketP1"], ["P2 threshold (≥)", "bucketP2"]] as const).map(([label, key]) => (
                <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "3px 0" }}>
                  <span style={{ fontSize: 12.5, color: "#465562" }}>{label}</span>
                  {numInput(cfg[key], (v) => setTop(key, v))}
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "3px 0" }}>
                <span style={{ fontSize: 12.5, color: "#465562" }}>Waiting-on-customer dampen (0–1)</span>
                {numInput(cfg.waitingDampen, (v) => setTop("waitingDampen", v))}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button className="sn-refresh" disabled={busy} onClick={onClose}>Cancel</button>
            <button className="sn-solid" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save weights"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function UrgentTab({ date }: { date: string }) {
  const [data, setData] = useState<UrgentResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(false); // manual ticket-watch run
  const [seg, setSeg] = useState<UrgentSeg>("all");
  const [person, setPerson] = useState<string | null>(null); // assigneeAccountId|name
  const [, force] = useState(0); // re-render the "updated Ns ago" label
  const [showWeights, setShowWeights] = useState(false); // admin scoring editor
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const isAdmin = profile?.role === "admin";

  const load = useCallback((signal?: AbortSignal) => {
    getStandupUrgent(date || undefined, signal)
      .then((d) => { setData(d); setErr(d.error ?? null); })
      .catch((e) => { if (!signal?.aborted) setErr(e instanceof Error ? e.message : "Failed to load urgent tickets."); })
      .finally(() => { if (!signal?.aborted) { setLoading(false); setRefreshing(false); } });
  }, [date]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true); setData(null); setErr(null);
    load(ctrl.signal);
    const poll = setInterval(() => load(), POLL_MS);
    const tick = setInterval(() => force((n) => n + 1), 15_000);
    return () => { ctrl.abort(); clearInterval(poll); clearInterval(tick); };
  }, [load]);

  // Audit-sweep progress: which tickets were expanded (reviewed) today.
  // Local per-browser, keyed by board date — resets naturally each morning.
  const [reviewed, setReviewed] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!data?.date) return;
    try {
      setReviewed(new Set(JSON.parse(localStorage.getItem(`brain.tickets.reviewed:${data.date}`) ?? "[]") as string[]));
    } catch {
      setReviewed(new Set());
    }
  }, [data?.date]);
  const markReviewed = useCallback((key: string) => {
    setReviewed((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      if (data?.date) localStorage.setItem(`brain.tickets.reviewed:${data.date}`, JSON.stringify([...next]));
      return next;
    });
  }, [data?.date]);

  if (loading) return <Spinner />;
  if (err && !data) return <div style={{ color: "#a13a40", fontSize: 14 }}>{err}</div>;
  if (!data) return <div className="sn-empty">No urgent ticket data.</div>;

  const sb = data.scoreboard;

  // The flat urgent set: prefer the backend's worst-first `urgent` list; else
  // derive it from the section lists (breached-active + imminent + at-risk).
  const flatUrgent: UrgentTicket[] = data.urgent ?? [
    ...data.breachedActive,
    ...data.imminent,
    ...data.atRisk,
  ];
  const totalUrgent = flatUrgent.length;

  // People present in the urgent set, for the single-select chips.
  const peopleMap = new Map<string, { id: string; name: string }>();
  for (const t of flatUrgent) {
    if (!t.assigneeName) continue;
    const id = t.assigneeAccountId ?? t.assigneeName;
    if (!peopleMap.has(id)) peopleMap.set(id, { id, name: t.assigneeName });
  }
  const chipPeople = [...peopleMap.values()].sort((a, b) => a.name.localeCompare(b.name));

  // Client-side filter by segmented control + selected person. "All open" is
  // the audit sweep: EVERY open ticket, not just the urgent tiers.
  const baseList = seg === "open" ? (data.tickets ?? flatUrgent) : flatUrgent;
  const filtered = baseList.filter((t) => {
    if (seg === "breached" && t.tier !== "breached") return false;
    if (seg === "imminent" && t.tier !== "imminent") return false;
    if (person) {
      const id = t.assigneeAccountId ?? t.assigneeName ?? "";
      if (id !== person) return false;
    }
    return true;
  });
  const reviewedShown = filtered.filter((t) => reviewed.has(t.key)).length;

  const refresh = () => { setRefreshing(true); load(); };

  // Manual ticket-watch run — fire-and-forget; the poll picks up the new row.
  const runNow = () => {
    setRunning(true);
    runTicketWatch()
      .then(() => enqueueSnackbar("Ticket watch started — new results land here in a few minutes.", { variant: "success" }))
      .catch((e) => enqueueSnackbar(e instanceof Error ? e.message : "Failed to start ticket watch.", { variant: "error" }))
      .finally(() => setRunning(false));
  };

  // Today's priorities = the highest-SCORED open tickets, so the section always
  // agrees with the P1/P2/P3 severity bar (the P1s lead). The nightly brief only
  // lends its one-line "why" phrasing, keyed by ticket.
  const briefByKey = new Map((data.brief ?? []).map((b) => [b.key, b] as const));
  const topPriorities = (data.tickets ?? flatUrgent).slice(0, PRIORITY_COUNT);

  // "View all tickets →" — the Jira project board, derived from any ticket's
  // base URL (…/browse/KEY). Falls back to "#" if no ticket is present.
  const sampleUrl = flatUrgent[0]?.url ?? "";
  const jiraBase = sampleUrl.includes("/browse/") ? sampleUrl.split("/browse/")[0] : "";
  const viewAllHref = jiraBase
    ? `${jiraBase}/issues/?jql=${encodeURIComponent("project = TICKET AND statusCategory != Done ORDER BY priority DESC")}`
    : "#";

  return (
    <div>
      {/* HEADER */}
      <div className="sn-uhd">
        <div>
          <div className="sn-uhd-title">Urgent tickets</div>
          <div className="sn-uhd-sub">Standup · SLA overview</div>
        </div>
        <div className="sn-uhd-right">
          <span className="sn-uhd-upd">
            Updated {fmtAgo(data.fetchedAt)}
            {data.watchGeneratedAt ? ` · AI audit ${fmtAgo(data.watchGeneratedAt)}` : ""}
          </span>
          {isAdmin && (
            <button className={`sn-tbtn${showWeights ? " active" : ""}`} onClick={() => setShowWeights((v) => !v)} title="Edit the priority-scoring weights">
              <i className="ti ti-adjustments" />Scoring
            </button>
          )}
          <button className={`sn-tbtn${running ? " spin" : ""}`} disabled={running} onClick={runNow} title="Re-run the nightly ticket audit now">
            <i className="ti ti-sparkles" />Run audit
          </button>
          <button className={`sn-tbtn pri${refreshing ? " spin" : ""}`} disabled={refreshing} onClick={refresh}>
            <i className="ti ti-refresh" />Refresh
          </button>
        </div>
      </div>

      {err && <div style={{ color: "#a13a40", fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {/* ADMIN — priority-scoring weights editor. */}
      {isAdmin && showWeights && <ScoringPanel onClose={() => setShowWeights(false)} onSaved={refresh} />}

      {/* SEVERITY SUMMARY BAR — P1/P2/P3 counts + stacked proportion bar. */}
      {(data.tickets?.length ?? 0) > 0 && <SeverityBar sb={sb} />}

      {/* TODAY'S PRIORITIES — highest-scored open tickets (P1s lead). */}
      {topPriorities.length > 0 && (
        <BriefCard tickets={topPriorities} briefByKey={briefByKey} drafts={data.drafts} reviewed={reviewed} onOpen={markReviewed} />
      )}

      {/* DATE COMMITMENTS — hard deadlines (text-extracted or duedate), never
          hidden by stuck-in-eng. Pinned above everything else. */}
      {(data.dateCommitments?.length ?? 0) > 0 && (
        <div className="sn-card" style={{ padding: "20px 23px", marginBottom: 14 }}>
          <div className="sn-na-hd">
            <div className="sn-na-title"><i className="ti ti-calendar-exclamation" /> Date commitments</div>
          </div>
          <TicketColHeader />
          <div className="sn-narows">
            {data.dateCommitments!.map((t) => <UrgentRow key={t.key} t={t} draft={data.drafts?.[t.key]} reviewed={reviewed.has(t.key)} onOpen={markReviewed} />)}
          </div>
        </div>
      )}

      {/* NEEDS REPLY — every ticket the customer is waiting on, longest wait
          first. Never maskable by status: nothing falls through the net. */}
      {(data.needsReply?.length ?? 0) > 0 && (
        <div className="sn-card" style={{ padding: "20px 23px", marginBottom: 14 }}>
          <div className="sn-na-hd">
            <div className="sn-na-title"><i className="ti ti-message-circle-exclamation" /> Needs reply — customer waiting</div>
            <span className="sn-count-amber">{data.needsReply!.length}</span>
          </div>
          <TicketColHeader />
          <div className="sn-narows">
            {data.needsReply!.map((t) => <UrgentRow key={t.key} t={t} draft={data.drafts?.[t.key]} reviewed={reviewed.has(t.key)} onOpen={markReviewed} />)}
          </div>
        </div>
      )}

      {/* OVERNIGHT DIGEST */}
      {(data.digest?.length ?? 0) > 0 && <OvernightCard date={data.date} items={data.digest!} />}

      {/* NEEDS ATTENTION NOW */}
      <div className="sn-card" style={{ padding: "20px 23px" }}>
        <div className="sn-na-hd">
          <div className="sn-na-title">Needs attention now</div>
          <div className="sn-seg" role="tablist" aria-label="Urgency filter">
            {([["all", "All urgent"], ["breached", "Breached"], ["imminent", "Imminent"], ["open", `All open (${(data.tickets ?? flatUrgent).length})`]] as [UrgentSeg, string][]).map(([k, lbl]) => (
              <button key={k} type="button" role="tab" aria-selected={seg === k}
                className={`sn-segbtn${seg === k ? " active" : ""}`} onClick={() => setSeg(k)}>{lbl}</button>
            ))}
          </div>
        </div>

        {chipPeople.length > 0 && (
          <div className="sn-chips" style={{ marginBottom: 14 }}>
            {chipPeople.map((p) => {
              const sel = person === p.id;
              return (
                <button key={p.id} type="button" className={`sn-pchip${sel ? " sel" : ""}`}
                  aria-pressed={sel} onClick={() => setPerson(sel ? null : p.id)}>
                  <span className="sn-pchip-av" style={{ background: colorFor(p.id) }}>{initials(p.name)}</span>
                  {firstName(p.name)}
                  {sel && <span className="sn-pchip-x" aria-hidden="true">×</span>}
                </button>
              );
            })}
          </div>
        )}

        {filtered.length > 0 && <TicketColHeader />}
        <div className="sn-narows">
          {filtered.length === 0
            ? <div className="sn-empty">{baseList.length === 0 ? "Nothing urgent right now ✓" : "No tickets match this filter."}</div>
            : filtered.map((t) => <UrgentRow key={t.key} t={t} draft={data.drafts?.[t.key]} reviewed={reviewed.has(t.key)} onOpen={markReviewed} />)}
        </div>

        {/* FOOTER */}
        <div className="sn-ufoot">
          <span className="sn-ufoot-txt">
            Showing <b>{filtered.length}</b> of <b>{seg === "open" ? baseList.length : totalUrgent}</b>{seg === "open" ? " open" : " urgent"} · <b>{sb.breached}</b> breached, <b>{sb.imminent}</b> imminent · reviewed <b>{reviewedShown}</b>/<b>{filtered.length}</b>
          </span>
          <a className="sn-viewall" href={viewAllHref} target="_blank" rel="noreferrer">
            View all tickets <i className="ti ti-arrow-right" />
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header band — live clock
// ---------------------------------------------------------------------------

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "Pacific/Auckland" });
  const date = now.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Pacific/Auckland" });
  return (
    <div>
      <div className="sn-clock">{time}</div>
      <div className="sn-date">{date}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const BRAIN_KEY = "brain";
const URGENT_KEY = "__urgent__";
const PROJECTS_KEY = "projects";

export function StandupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();
  const [board, setBoard] = useState<StandupBoard | null>(null);
  const [loading, setLoading] = useState(true);
  // Per-person SLA dot is still computed (kept for reuse) but no longer shown on
  // the facepile picker — the picker is now plain avatars per the design.
  const [, setDots] = useState<Record<string, DotColor>>({});

  const refresh = useCallback(async () => {
    try { setBoard(await getStandupBoard()); }
    catch (err) {
      setBoard((prev) => prev ?? { date: "", members: [] });
      enqueueSnackbar(`Failed to load standup board: ${err instanceof Error ? err.message : String(err)}`, { variant: "error" });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    let cancelled = false;
    refresh().finally(() => { if (!cancelled) setLoading(false); });
    const poll = setInterval(() => void refresh(), POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(poll); document.removeEventListener("visibilitychange", onVisible); };
  }, [refresh]);

  // Prewarm the Aurora-backed Projects data on page load. The Serverless v2 cluster
  // scales to zero and the first query cold-resumes (~5-15s) — kick it off now (admin
  // endpoints) so the Projects tab is warm + cached by the time it's clicked, not 15s slow.
  useEffect(() => {
    if (profile?.role !== "admin") return;
    void getAaronProjects({ focusOnly: true }).catch(() => {});
    void getAaronVersion().catch(() => {});
  }, [profile?.role]);

  const onTickets = useCallback((userId: string, t: StandupTickets) => {
    setDots((d) => ({ ...d, [userId]: dotFor(t) }));
  }, []);

  // People = board members excluding any brain entry; the Brain pill is appended.
  const people = useMemo(() => (board?.members ?? []).filter((m) => m.kind !== "brain"), [board]);
  const raw = decodeURIComponent(location.hash.replace(/^#/, ""));
  const defaultKey = people[0]?.userId ?? BRAIN_KEY;
  const isBrain = raw === BRAIN_KEY || raw === "review" || raw === "questions";
  const isUrgent = raw === URGENT_KEY;
  const isProjects = raw === PROJECTS_KEY;

  // Agent page-awareness: the standup companion (W3) resolves "yesterday" /
  // "what did <person> commit to" via get_standup for this snapshot's date.
  const { setPageContext } = useAgent();
  useEffect(() => {
    setPageContext({ route: "/standup", tab: "standup" });
    return () => setPageContext(null);
  }, [setPageContext]);
  useAgentSnapshot(board ? {
    date: board.date,
    section: raw || "team",
    people: people.slice(0, 12).map((m) => m.displayName),
  } : null);
  const isKnownPerson = people.some((m) => m.userId === raw);
  const active = isUrgent ? URGENT_KEY : isBrain ? BRAIN_KEY : isProjects ? PROJECTS_KEY : (isKnownPerson ? raw : defaultKey);
  const setTab = useCallback((key: string) => navigate(`/standup#${encodeURIComponent(key)}`), [navigate]);

  const isAdmin = profile?.role === "admin";

  if (loading) return <div className="kt-page"><StandupStyles /><Spinner /></div>;

  const activeMember = people.find((m) => m.userId === active);
  const canEdit = active === BRAIN_KEY
    ? isAdmin
    : !!activeMember && (isAdmin || profile?.userId === activeMember.userId);

  const band = (
    <div className="sn-band">
      <Clock />
      <div className="sn-band-right">
        {/* Standup is fully automatic — a Google Meet transcript triggers analysis.
            No manual open/close; the pipeline runs on ingest (see standup-pipeline). */}
        <div className="sn-chip open" title="A Google Meet transcript triggers analysis automatically — no manual close.">
          <div className="sn-chip-dot" />
          <span className="sn-chip-label">Automatic</span>
        </div>
        <button className="sn-statebtn closed" onClick={() => navigate("/standup/history")}>
          Standup history
        </button>
      </div>
    </div>
  );

  // Chrome-style unified tab strip: Tickets · Brain · | · Alex · Asha · …
  // The active tab shares the content panel's background and interrupts the
  // keyline beneath it; the strip is a container so person-tab names hide
  // progressively as it tightens (it never horizontal-scrolls).
  const rail = (
    <div className="sn-tabwrap">
      <div className="sn-tabstrip" role="tablist" aria-label="Standup views and team">
        <button type="button" role="tab" aria-selected={active === URGENT_KEY}
          className={`sn-tab view urgent${active === URGENT_KEY ? " active" : ""}`} onClick={() => setTab(URGENT_KEY)}>
          <Icon name="ticket" size={16} />
          <span className="sn-tab-vlabel">Tickets</span>
        </button>
        <button type="button" role="tab" aria-selected={active === BRAIN_KEY}
          className={`sn-tab view brain${active === BRAIN_KEY ? " active" : ""}`} onClick={() => setTab(BRAIN_KEY)}>
          <Icon name="brain" size={16} />
          <span className="sn-tab-vlabel">Brain</span>
        </button>
        <button type="button" role="tab" aria-selected={active === PROJECTS_KEY}
          className={`sn-tab view${active === PROJECTS_KEY ? " active" : ""}`} onClick={() => setTab(PROJECTS_KEY)}>
          <Icon name="folders" size={16} />
          <span className="sn-tab-vlabel">Projects</span>
        </button>

        <span className="sn-tabdiv" aria-hidden="true" />

        {people.map((m) => {
          const sel = active === m.userId;
          const tint = avatarTint(m.userId);
          return (
            <button key={m.userId} type="button" role="tab" aria-selected={sel} aria-label={m.displayName}
              data-tip={sel ? undefined : firstName(m.displayName)}
              className={`sn-tab person${sel ? " active" : ""}`} onClick={() => setTab(m.userId)}>
              <span className="sn-tab-av" style={{ background: tint.bg, color: tint.fg }}>{initials(m.displayName)}</span>
              <span className="sn-tab-name">{firstName(m.displayName)}</span>
            </button>
          );
        })}
        <span className="sn-tabspacer" aria-hidden="true" />
      </div>
    </div>
  );

  return (
    <div className="sn-page">
      <StandupStyles />
      {band}
      {rail}
      {active === URGENT_KEY
        ? <UrgentTab date={board?.date ?? ""} />
        : active === BRAIN_KEY
        ? <BrainTab canEdit={canEdit} />
        : active === PROJECTS_KEY
        ? <ProjectsTab people={people.map((m) => ({ userId: m.userId, displayName: m.displayName }))} date={board?.date ?? ""} canEdit={isAdmin} />
        : activeMember
          ? <PersonTab member={activeMember} date={board?.date ?? ""} canEdit={canEdit} emphasis="balanced" onSaved={() => void refresh()} onTickets={onTickets} />
          : <div className="sn-empty">No one on the board yet. Map teammates to Jira in Settings to populate the standup board.</div>}
    </div>
  );
}
